/**
 * Root application component — multi-gateway wired.
 *
 * Manages top-level screen routing and orchestrates all modules:
 * - Gateway setup → connection → handshake → pairing (per gateway)
 * - All gateways connect simultaneously
 * - One gateway is "focused" for text/voice input
 * - Chat shows messages from all gateways with gateway badges
 * - Voice: VAD → STT (Whisper or Web Speech) → text chat.send → streaming → TTS → playback
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import type { ChatMessage, TtsDebugSentence } from './types/chat';
import type { GatewayAgent, SessionRow } from './types/sessions';
import { GatewayProvider } from './gateway/store';
import { useGateway } from './hooks/useGateway';
import { useAuth } from './auth/AuthContext';
import { GatewaySetup } from './components/GatewaySetup';
import { GatewayBar } from './components/GatewayBar';
import { PairingApproval } from './components/PairingApproval';
import { OriginBlockedOverlay } from './components/OriginBlockedOverlay';
import { Login } from './components/Login';
import { AuthVerify } from './components/AuthVerify';
import { Welcome } from './components/Welcome';
import { Chat } from './components/Chat';
import { Orb } from './components/Orb';
import { Settings } from './components/Settings';
import { SessionPanel } from './components/SessionPanel';
import { SessionQuickSwitchRail } from './components/SessionQuickSwitchRail';
import { AgentTicker } from './components/AgentTicker';
import { PrivacyExitButton, usePrivacyMode } from './components/PrivacyMode';
import { useLayout } from './hooks/useLayout';
import { useViewRouter } from './hooks/useViewRouter';
import { ViewSwitcher } from './components/ViewSwitcher';
import { TaskBoard } from './components/TaskBoard';
import { useFSM, getFSMActor } from './hooks/useFSM';
import { useWakeLock } from './hooks/useWakeLock';
import { useChat } from './hooks/useChat';
import { useVoice, type VoiceSegmentInput } from './hooks/useVoice';
import { useVoiceSpeakingEffects } from './hooks/useVoiceSpeakingEffects';
import { useSessionManager } from './hooks/useSessionManager';
import { useSessionQuickSwitch } from './hooks/useSessionQuickSwitch';
import { useAgentTicker } from './hooks/useAgentTicker';
import { useVoiceLock } from './hooks/useVoiceLock';
import { useKeyboardVisible } from './hooks/useKeyboardVisible';
import { VoiceLockTest } from './components/VoiceLockTest';
import {
  ShutdownBanner,
  GatewayUpdateBadge,
  ExecApprovalNotification,
  PresenceIndicator,
} from './components/GatewayEventBanner';
import { useCloudGatewaySync } from './hooks/useCloudGatewaySync';
import { useGatewayEvents } from './hooks/useGatewayEvents';
import { StreamingHandler } from './chat/streaming';
import {
  sendChatMessage,
  steerSessionMessage,
  abortChat,
  fetchChatHistory,
  fetchChatHistoryAsMessages,
  historyMessageToChatMessage,
  switchModel as switchModelWS,
} from './chat';
import {
  getAssistantMessageKind,
  shouldRenderFinalOnlyAssistantMessage,
  summarizeAssistantMessage,
} from './chat/assistantMessage';
import { injectFinalOnlyAssistantMessage } from './chat/finalOnly';
import {
  normalizeSendWhileRunningMode,
  planSendWhileRunning,
  stripQueueDirectiveForDisplay,
} from './chat/sendWhileRunning';
import {
  appendToolCallToMessages,
  markAllToolCallsDone,
  markLatestToolCallDone,
  updateToolCallProgress,
} from './chat/toolCalls';
import { ensureAudioContextResumed, unlockAudio, duckTtsGain, restoreTtsGain } from './voice/audio';
import { playAudioCue } from './voice/audioCues';
import { TurnArbiter } from './voice/turnArbiter';
import type { TurnCancelMeta } from './voice/turnArbiter';
import { TurnProtocolManager } from './voice/turnProtocol';
import { BargeInManager } from './voice/bargeIn';
import {
  VoiceCommandTurnController,
  parseVoiceCommand,
  parseVoiceControlCommand,
  resolveVoiceLockSwitchOnlyCommand,
  routeVoiceTurnSegment,
  stepPlaybackSpeed,
  type ParsedVoiceCommand,
  type ResolvedVoiceTurnCommand,
  type VoiceTurnSegmentDecision,
} from './voice/commands';
import {
  buildSessionReadCursor,
  buildSessionReadCursorFromMessage,
  buildSessionReadoutPlan,
  hasSessionReadCursorMatch,
  type SessionReadCursor,
} from './voice/sessionReadout';
import type { PairingState } from './auth/pairing';
import type { WSClient } from './ws/client';
import type { GatewayConfig } from './types/gateway';
import { DEFAULT_SESSION_KEY, STORAGE_KEYS, getInitialSessionKey } from './utils/constants';
import { getApiAuthHeaders } from './utils/auth';
import { sessionDisplayName, sessionVoiceIdentityName } from './utils/sessionDisplay';
import { readJSON, writeJSON, writeString } from './utils/storage';
import { remoteLog, notifyStartupComplete } from './utils/remoteLog';
import { getErrorMessage } from './utils/errors';

declare const __FRONTEND_VERSION__: string;
import { getEnvironmentBadge, getApiUrl } from './utils/environment';
import {
  buildChatAttachment,
  buildChatUploadBatchMessage,
  getChatAttachmentInlineMaxBytes,
  uploadOneFile,
  type UploadedFileMeta,
} from './upload';
import { resolveMessageFieldContextWindow } from './utils/modelContext';
import { reloadApp } from './utils/reload';
import { handleGitHubCallback } from './utils/github';
import { getCurrentPreviewPrNumber, markPreviewBootSuccess } from './utils/previewBuild';
import { useVersion, forceUpdate } from './hooks/useVersion';
import { collectCloudSettings, restoreCloudSettings, useSetting } from './settings';
import styles from './App.module.css';
import './App.css';

/** Capacitor bridge types for Android native plugins */
declare global {
  interface Window {
    Capacitor?: {
      Plugins?: {
        BackgroundMic?: {
          start: () => Promise<void>;
          stop: () => Promise<void>;
        };
        [key: string]: unknown;
      };
      [key: string]: unknown;
    };
  }
}

/** Start Android foreground service (no-op on web) */
function startForegroundService(): void {
  window.Capacitor?.Plugins?.BackgroundMic?.start()
    ?.then(() => console.log('[App] Foreground service started'))
    ?.catch((e: unknown) => console.warn('[App] Foreground service start failed:', e));
}

/** Stop Android foreground service (no-op on web) */
function stopForegroundService(): void {
  window.Capacitor?.Plugins?.BackgroundMic?.stop()
    ?.then(() => console.log('[App] Foreground service stopped'))
    ?.catch((e: unknown) => console.warn('[App] Foreground service stop failed:', e));
}

type Screen = 'setup' | 'pairing' | 'app';

/** Format a token count in short form: 1,234 → "1.2k", 45000 → "45k", 200000 → "200k", 1000000 → "1M" */
function fmtTokens(n: number): string {
  if (n < 1000) return n.toLocaleString();
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return m === Math.floor(m) ? `${m}M` : `${Math.round(m * 10) / 10}M`;
  }
  const k = n / 1000;
  if (k >= 10) return `${Math.round(k)}k`;
  return `${Math.round(k * 10) / 10}k`;
}



/**
 * Extract the most relevant argument from a tool call's input for display.
 * Returns the full string — truncation is handled at the UI layer.
 */
function extractToolArg(name: string, input: unknown): string | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const obj = input as Record<string, unknown>;

  let arg: string | undefined;

  // Tool-specific key fields
  if (name === 'exec' && typeof obj.command === 'string') {
    arg = obj.command;
  } else if (
    (name === 'read' || name === 'write' || name === 'edit') &&
    typeof (obj.path ?? obj.file_path) === 'string'
  ) {
    arg = (obj.path ?? obj.file_path) as string;
  } else if (name === 'web_search' && typeof obj.query === 'string') {
    arg = obj.query;
  } else if (name === 'web_fetch' && typeof obj.url === 'string') {
    arg = obj.url;
  } else {
    // Fallback: first short string value
    for (const v of Object.values(obj)) {
      if (typeof v === 'string' && v.length > 0 && v.length < 200) {
        arg = v;
        break;
      }
    }
  }

  if (!arg) return undefined;
  return arg;
}

function buildWsMessageSummary(content: string): {
  rawContentKind: string;
  contentLen: number;
  contentPreview: string;
} {
  const summary = summarizeAssistantMessage(content);
  return {
    rawContentKind: getAssistantMessageKind(content),
    contentLen: summary.length,
    contentPreview: summary.preview,
  };
}

function buildSessionReadCursorKey(gatewayId: string, sessionKey: string): string {
  return `${gatewayId}::${sessionKey}`;
}

function collectVisibleActiveSessionMessages(
  messages: ChatMessage[],
  gatewayId: string,
): ChatMessage[] {
  return messages.filter((message) => {
    const matchesGateway = !message.gatewayId || message.gatewayId === gatewayId;
    return matchesGateway && !message.agentLabel;
  });
}

function buildAutoReasoningSessionKey(gatewayId: string, sessionKey: string): string {
  return `${gatewayId}::${sessionKey}`;
}

const SESSION_READOUT_HISTORY_PAGE_SIZE = 30;

interface PendingSessionReadout {
  cursorKey: string;
  gatewayId: string;
  sessionKey: string;
  unreadMessages: ChatMessage[];
  speechQueue: string[];
  trackedSpeechOffset: number;
}

interface PendingVoiceSwitchAnnouncement {
  gatewayId: string;
  sessionKey: string;
  text: string;
}

function getAgentDisplayNameForSessionKey(sessionKey: string, agents: GatewayAgent[]): string | undefined {
  const match = sessionKey.match(/^agent:([^:]+):/u);
  if (!match) {
    return undefined;
  }

  const agentId = match[1] ?? '';
  const agent = agents.find((entry) => entry.id === agentId);
  return agent?.identity?.name ?? agent?.name ?? agent?.id;
}

function isMainSessionKey(sessionKey: string): boolean {
  return /^agent:[^:]+:main$/u.test(sessionKey);
}

function buildVoiceSessionLabel(
  session: SessionRow | undefined,
  sessionKey: string,
  agents: GatewayAgent[],
): string {
  const agentDisplayName = getAgentDisplayNameForSessionKey(sessionKey, agents);
  const label = isMainSessionKey(sessionKey)
    ? sessionVoiceIdentityName(sessionKey, agentDisplayName)
    : session
      ? sessionDisplayName(session, agentDisplayName)
      : sessionVoiceIdentityName(sessionKey, agentDisplayName);
  return `${label} session`;
}

/**
 * Inner app component that uses gateway context.
 */
function AppInner({ onShowLogin }: { onShowLogin?: () => void }) {
  // ── Migrate auto-show-subagents → subagent-depth ──
  if (localStorage.getItem('auto-show-subagents') !== null && localStorage.getItem(STORAGE_KEYS.SUBAGENT_DEPTH) === null) {
    if (localStorage.getItem('auto-show-subagents') === 'true') {
      localStorage.setItem(STORAGE_KEYS.SUBAGENT_DEPTH, '3');
    }
    localStorage.removeItem('auto-show-subagents');
  }

  // ── Layout & View Routing ──
  const { isMobile, isDesktop } = useLayout();
  const { activeView, switchView } = useViewRouter();
  const isKeyboardVisible = useKeyboardVisible();
  const [quickSwitchSideSetting] = useSetting(STORAGE_KEYS.SESSION_QUICK_SWITCH_SIDE);
  const [sessionSwitchReadoutSetting] = useSetting(STORAGE_KEYS.SESSION_SWITCH_READOUT);
  const [sendWhileRunningSetting] = useSetting(STORAGE_KEYS.SEND_WHILE_RUNNING_MODE);
  const quickSwitchSide = quickSwitchSideSetting === 'left' ? 'left' : 'right';
  const readUnheardMessagesOnSessionSwitch = sessionSwitchReadoutSetting !== 'false';
  const sendWhileRunningMode = normalizeSendWhileRunningMode(sendWhileRunningSetting);

  // Suppress unused-var warnings — will be used for layout refinements
  void isMobile;
  void isDesktop;

  // ── Auth state (for Welcome screen awareness) ──
  const { isAuthenticated, user } = useAuth();

  // ── Version ──
  const { version, updateAvailable } = useVersion();
  const versionLabel = version ? `v${version}` : 'v…';
  const previewPrNumber = getCurrentPreviewPrNumber();
  const previewBadge = previewPrNumber ? (
    <span className={styles.envBadge} style={{ background: '#a855f7' }}>
      PREVIEW #{previewPrNumber}
    </span>
  ) : null;

  useEffect(() => {
    markPreviewBootSuccess();
  }, []);

  // ── Gateway (multi-gateway) ──
  const {
    gateways,
    activeGateway,
    connectionState,
    addGateway,
    removeGateway: removeGatewayAction,
    switchGateway,
    connectAll,
    isConnected,
    isAnyConnected,
    isPairing,
    wsClient,
    getClient,
    connectionStates,
    reconnect,
    gatewayVersion,
    activeGatewayCaps,
  } = useGateway();

  // ── FSM ──
  const {
    displayState,
    statusText,
    send,
    isActivated,
    isRequestActive,
    // isPlaying, // TODO: restore when stop button is re-enabled
    isSpeakerMuted,
    isMicMuted,
    value: fsmValue,
    context: fsmContext,
  } = useFSM();

  // ── Wake Lock (keep screen on while voice session is active) ──
  useWakeLock(isActivated);

  // ── Chat ──
  const chat = useChat();
  const {
    messages,
    selectedModel,
    addMessage,
    sendMessage: chatSendMessage,
    patchPlaceholderRunId,
    updateStreaming,
    finalizeStreaming,
    abortStreaming,
    errorStreaming,
    loadHistory,
    switchModel: chatSwitchModel,
  } = chat;

  // ── Session Manager ──
  const sessionSwitchRef = useRef<(gwId: string, sessionKey: string) => void>(() => {});
  const maybeStartPendingSessionReadoutRef = useRef<() => void>(() => {});
  const sessionManager = useSessionManager({
    gateways,
    activeGatewayId: activeGateway?.id ?? null,
    connectionStates,
    getClient,
    onSessionSwitch: useCallback((gwId: string, sessionKey: string) => {
      sessionSwitchRef.current(gwId, sessionKey);
    }, []),
  });
  const sessionQuickSwitch = useSessionQuickSwitch({
    gatewayId: activeGateway?.id ?? null,
    sessions: sessionManager.activeSessionData?.sessions ?? [],
    activeSessionKey: sessionManager.activeSessionKey,
    sessionListReady: sessionManager.activeSessionData?.lastFetched != null,
    agents: sessionManager.agents,
  });
  const activeManagedSessionKey = sessionManager.activeSessionKey;
  const managedSessions = sessionManager.activeSessionData?.sessions ?? [];
  const overviewSessionsByAgent = useMemo(() => {
    const grouped = new Map<string, SessionRow[]>();
    for (const session of sessionQuickSwitch.visibleSessions) {
      const parts = session.key.split(':');
      const agentId = parts.length >= 2 && parts[0] === 'agent' ? parts[1] : 'unknown';
      const list = grouped.get(agentId) ?? [];
      list.push(session);
      grouped.set(agentId, list);
    }
    return grouped;
  }, [sessionQuickSwitch.visibleSessions]);
  const findManagedSession = useCallback(
    (key: string) => managedSessions.find((session) => session.key === key),
    [managedSessions],
  );
  const activeManagedSession = useMemo(
    () => findManagedSession(activeManagedSessionKey),
    [activeManagedSessionKey, findManagedSession],
  );
  const messageFieldContextWindow = useMemo(
    () => resolveMessageFieldContextWindow({
      selectedModel,
      models: sessionManager.models,
      activeSessionModel: activeManagedSession?.model,
      activeSessionContextTokens: activeManagedSession?.contextTokens,
      defaultModel: sessionManager.defaults?.model,
      defaultContextTokens: sessionManager.defaults?.contextTokens,
    }),
    [
      selectedModel,
      sessionManager.models,
      sessionManager.defaults?.model,
      sessionManager.defaults?.contextTokens,
      activeManagedSession?.model,
      activeManagedSession?.contextTokens,
    ],
  );
  const chooseOverviewFallbackSessionKey = useCallback((removedSessionKey: string): string | null => {
    const remainingSessions = sessionQuickSwitch.visibleSessions.filter((session) => session.key !== removedSessionKey);
    const defaultAgentId = sessionManager.activeSessionData?.defaultAgentId ?? 'main';
    const parseAgentId = (sessionKey: string): string | null => {
      const parts = sessionKey.split(':');
      if (parts.length < 2 || parts[0] !== 'agent') return null;
      return parts[1] ?? null;
    };
    const buildMainKey = (agentId: string) => `agent:${agentId}:main`;

    const removedAgentId = parseAgentId(removedSessionKey);
    if (removedAgentId) {
      const sameAgentMain = remainingSessions.find((session) => session.key === buildMainKey(removedAgentId));
      if (sameAgentMain) return sameAgentMain.key;

      const sameAgentNewest = [...remainingSessions]
        .filter((session) => parseAgentId(session.key) === removedAgentId)
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))[0];
      if (sameAgentNewest) return sameAgentNewest.key;

      return buildMainKey(removedAgentId);
    }

    const defaultMain = remainingSessions.find((session) => session.key === buildMainKey(defaultAgentId));
    if (defaultMain) return defaultMain.key;

    return [...remainingSessions]
      .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))[0]?.key ?? buildMainKey(defaultAgentId);
  }, [sessionManager.activeSessionData?.defaultAgentId, sessionQuickSwitch.visibleSessions]);
  const autoReasoningStreamSessionsRef = useRef<Set<string>>(
    new Set(readJSON<string[]>(STORAGE_KEYS.AUTO_REASONING_STREAM_SESSIONS, [])),
  );

  // ── Agent Ticker (subagent monitoring) ──
  const agentTicker = useAgentTicker({
    gateways,
    connectionStates,
    getClient,
  });

  // ── Cloud Gateway Sync (auto-upload on gateway changes) ──
  useCloudGatewaySync();

  // ── Gateway Events (shutdown, presence, update, approvals) ──
  const gatewayEvents = useGatewayEvents();

  // ── Reasoning Stream Migration ──
  // Hotfix: the Talkyn voice/web client connects as a webchat client, and the
  // gateway rejects sessions.patch for that surface. The old auto-migration
  // tried to set reasoningLevel="stream" on first connect for fresh sessions,
  // which spammed invalid sessions.patch calls and broke main/new-session
  // flows after restarts. Leave reasoning at the server/default level here
  // instead of patching session config from the client.
  useEffect(() => {
    if (!activeGateway?.id || !activeManagedSession) return;

    const autoReasoningKey = buildAutoReasoningSessionKey(activeGateway.id, activeManagedSession.key);

    if (!autoReasoningStreamSessionsRef.current.has(autoReasoningKey)) {
      autoReasoningStreamSessionsRef.current.add(autoReasoningKey);
      writeJSON(
        STORAGE_KEYS.AUTO_REASONING_STREAM_SESSIONS,
        Array.from(autoReasoningStreamSessionsRef.current),
      );
    }
  }, [activeGateway?.id, activeManagedSession]);

  // ── Voice Transcript Handler ──
  // When the voice pipeline produces a transcript (from Whisper or Web Speech),
  // create a chat message and send it to the focused gateway as normal text.
  // Ref for mic muted state (used in closures)
  const wasListeningBeforeTrainingRef = useRef(false);
  const isMicMutedRef = useRef(isMicMuted);
  isMicMutedRef.current = isMicMuted;

  // ── Draft transcript bubble ID (stable across re-renders) ──
  const draftBubbleIdRef = useRef('draft-transcript');
  const voiceRouteSessions = sessionQuickSwitch.voiceRouteSessions;
  const voiceRouteAgents = sessionManager.agents;
  const activeVoiceSessionKey = sessionManager.activeSessionKey;
  const switchVoiceSession = sessionManager.switchSession;
  const voiceCommandTurnRef = useRef<VoiceCommandTurnController | null>(null);
  if (!voiceCommandTurnRef.current) {
    voiceCommandTurnRef.current = new VoiceCommandTurnController();
  }

  function setMicMutedByVoiceCommand(nextMuted: boolean): void {
    writeString(STORAGE_KEYS.MIC_MUTED, String(nextMuted));
    send({ type: nextMuted ? 'VOICE_COMMAND_MUTE' : 'VOICE_COMMAND_LISTEN' });
    remoteLog('toggle', 'mic_mute', { muted: nextMuted, source: 'voice_command' });

    if (!nextMuted && isActivated && !voice.isVADActive) {
      voice.startListening().catch((err) => {
        console.error('[App] Failed to restart listening on voice unmute:', err);
      });
    }
  }

  function setSpeakerMutedByVoiceCommand(nextMuted: boolean): void {
    writeString(STORAGE_KEYS.SPEAKER_MUTED, String(nextMuted));
    send({ type: nextMuted ? 'VOICE_COMMAND_SILENCE' : 'VOICE_COMMAND_SPEAK' });
    remoteLog('toggle', 'speaker_mute', { muted: nextMuted, source: 'voice_command' });

    if (nextMuted) {
      voice.stopSpeaking();
    }
  }

  function adjustPlaybackSpeedByVoiceCommand(direction: 'faster' | 'slower'): void {
    const previousSpeed = voice.playbackSpeed;
    const nextSpeed = stepPlaybackSpeed(previousSpeed, direction);
    voice.setPlaybackSpeed(nextSpeed);
    remoteLog('toggle', 'playback_speed', {
      source: 'voice_command',
      direction,
      previousSpeed,
      nextSpeed,
    });
  }

  function maybeHandleVoiceControlCommand(
    transcript: string,
    source: 'turn_arbiter' | 'legacy' | 'turn_cancel_fallback',
    extraLog?: Record<string, unknown>,
  ): boolean {
    const command = parseVoiceControlCommand(transcript);
    if (!command) {
      return false;
    }

    remoteLog('voice', 'control_command', {
      source,
      command: command.command,
      matchedPhrase: command.matchedPhrase,
      originalTextLen: command.originalText.length,
      isMicMuted,
      isSpeakerMuted,
      playbackSpeed: voice.playbackSpeed,
      ...extraLog,
    });

    switch (command.command) {
      case 'mute_mic':
        setMicMutedByVoiceCommand(true);
        return true;
      case 'unmute_mic':
        setMicMutedByVoiceCommand(false);
        return true;
      case 'mute_speaker':
        setSpeakerMutedByVoiceCommand(true);
        return true;
      case 'unmute_speaker':
        setSpeakerMutedByVoiceCommand(false);
        return true;
      case 'faster':
        adjustPlaybackSpeedByVoiceCommand('faster');
        return true;
      case 'slower':
        adjustPlaybackSpeedByVoiceCommand('slower');
        return true;
    }

    return false;
  }

  async function sendUserChatMessage(params: {
    sessionKey: string;
    displayMessage: string;
    gatewayId: string;
    gatewayName?: string;
    source: 'text' | 'voice' | 'upload';
    attachments?: Parameters<typeof sendChatMessage>[3] extends infer T ? T extends { attachments?: infer A } ? A : never : never;
    extraLog?: Record<string, unknown>;
  }): Promise<string> {
    if (!wsClient) {
      throw new Error('Gateway client not connected');
    }

    const requestRunId = crypto.randomUUID();
    const isActiveTargetSession = params.sessionKey === activeSessionKeyRef.current;
    const hadAnyActiveReply = isRequestActive;
    const hadActiveReply = isActiveTargetSession && isRequestActive;
    const isStreamingReplyActive = isActiveTargetSession && currentRunIdRef.current !== null;
    const effectiveSendWhileRunningMode = params.source === 'upload' ? 'queue' : sendWhileRunningMode;
    const plan = planSendWhileRunning({
      message: params.displayMessage,
      mode: effectiveSendWhileRunningMode,
      isReplyActive: hadActiveReply,
      isReplyStreaming: isStreamingReplyActive,
    });

    const localRunId = chatSendMessage(
      params.displayMessage,
      params.gatewayId,
      params.gatewayName,
      {
        requestRunId,
        createAssistantPlaceholder: plan.createAssistantPlaceholder,
      },
    );

    remoteLog('chat', 'send_while_running', {
      source: params.source,
      sessionKey: params.sessionKey,
      runId: requestRunId,
      localRunId,
      configuredMode: sendWhileRunningMode,
      effectiveMode: effectiveSendWhileRunningMode,
      transport: plan.transport,
      steersCurrentReply: plan.steersCurrentReply,
      createAssistantPlaceholder: plan.createAssistantPlaceholder,
      hadAnyActiveReply,
      hadActiveReply,
      isStreamingReplyActive,
      fallbackReason: plan.fallbackReason ?? null,
      textLen: params.displayMessage.length,
      ...params.extraLog,
    });

    if (plan.createAssistantPlaceholder) {
      send({ type: 'CHAT_REQUEST_SENT' });
    }

    try {
      const sendOptions = {
        idempotencyKey: requestRunId,
        ...(params.attachments ? { attachments: params.attachments } : {}),
      };
      const runId = plan.transport === 'sessions.steer'
        ? await steerSessionMessage(wsClient, params.sessionKey, plan.transportMessage, sendOptions)
        : await sendChatMessage(wsClient, params.sessionKey, plan.transportMessage, sendOptions);
      return runId;
    } catch (err) {
      const errorMessage = getErrorMessage(err);
      remoteLog('chat', 'send_while_running_error', {
        source: params.source,
        sessionKey: params.sessionKey,
        runId: requestRunId,
        configuredMode: sendWhileRunningMode,
        effectiveMode: effectiveSendWhileRunningMode,
        transport: plan.transport,
        hadAnyActiveReply,
        hadActiveReply,
        steersCurrentReply: plan.steersCurrentReply,
        error: errorMessage,
      });

      if (plan.createAssistantPlaceholder) {
        errorStreaming(requestRunId, errorMessage);
      } else {
        addMessage({
          id: `assistant-error-${requestRunId}`,
          role: 'assistant',
          content: `⚠️ ${errorMessage}`,
          timestamp: new Date().toISOString(),
          isHistory: false,
          isStreaming: false,
          ttsStatus: 'none',
          runId: requestRunId,
          gatewayId: params.gatewayId,
          gatewayName: params.gatewayName,
        });
      }

      if (!hadAnyActiveReply) {
        send({ type: 'SSE_ERROR' });
      }

      throw err;
    }
  }

  function finalizeCommittedVoiceTurn(): void {
    voiceCommandTurnRef.current?.reset();

    // FSM: mark turn as committed (returns input to idle, releases TTS deferral)
    send({ type: 'TURN_COMMITTED' });

    // Reset barge-in state on commit (new turn = clean slate)
    bargeInRef.current?.reset();

    // Flush deferred TTS that accumulated during the turn
    const actor = getFSMActor();
    const deferred = actor.getSnapshot().context.deferredTtsTexts;
    if (deferred.length > 0) {
      actor.send({ type: 'TTS_DEFERRED_FLUSH' });
      remoteLog('tts', 'flush_deferred_turn_commit', { count: deferred.length });
      const textsToSpeak = [...deferred];
      setTimeout(() => {
        for (const t of textsToSpeak) {
          voiceRef.current.speak(t);
        }
        maybeStartPendingSessionReadoutRef.current();
      }, 0);
      return;
    }

    maybeStartPendingSessionReadoutRef.current();
  }

  // ── Shared voice transcript submit path (turn arbiter + legacy fallback) ──
  function sendResolvedVoiceTranscript(
    command: ParsedVoiceCommand | ResolvedVoiceTurnCommand,
    source: 'turn_arbiter' | 'legacy' | 'turn_cancel_fallback',
    extraLog?: Record<string, unknown>,
  ): boolean {
    if (!wsClient || !isConnected || !activeGateway) return false;
    if (isMicMutedRef.current) return false;

    if (command.shouldSwitchSession) {
      const canSwitch = prepareVoiceSessionSwitch(command.targetSessionKey, {
        announceSuccess: !command.remainingText,
      });
      if (!canSwitch) {
        return false;
      }

      skipNextSessionReadoutRef.current = command.remainingText
        ? {
            gatewayId: activeGateway.id,
            sessionKey: command.targetSessionKey,
            loadId: null,
          }
        : null;
      switchVoiceSession(command.targetSessionKey);
      turnProtocolRef.current?.setSessionKey(command.targetSessionKey);
    } else if (!command.remainingText && command.kind === 'address') {
      const wasJustSwitchedByVoice = switchOnlyVoiceCommandSessionKeyRef.current === command.targetSessionKey;
      switchOnlyVoiceCommandSessionKeyRef.current = null;

      if (!wasJustSwitchedByVoice) {
        clearPendingVoiceSwitchAnnouncement();
        queueStandaloneSessionAnnouncement(
          command.targetSessionKey,
          formatVoiceSessionAnnouncement(command.targetSessionKey, 'already_on'),
        );
      }
    } else if (switchOnlyVoiceCommandSessionKeyRef.current === command.targetSessionKey) {
      cancelPendingSwitchOnlyVoiceFeedback(command.targetSessionKey);
      const pendingSwitchLoad = pendingSessionSwitchLoadRef.current;
      if (pendingSwitchLoad?.sessionKey === command.targetSessionKey) {
        skipNextSessionReadoutRef.current = {
          gatewayId: pendingSwitchLoad.gatewayId,
          sessionKey: command.targetSessionKey,
          loadId: pendingSwitchLoad.loadId,
        };
      }
    } else {
      switchOnlyVoiceCommandSessionKeyRef.current = null;
    }

    remoteLog('voice', 'command_parsed', {
      source,
      kind: command.kind,
      targetSessionKey: command.targetSessionKey,
      shouldSwitchSession: command.shouldSwitchSession,
      matchedAlias: command.kind === 'address' || command.kind === 'ambiguous'
        ? command.matchedAlias
        : undefined,
      matchedBy: command.kind === 'address' ? command.matchedBy : undefined,
      candidateSessionKeys: command.kind === 'ambiguous'
        ? command.candidateSessionKeys
        : undefined,
      originalTextLen: command.originalText.length,
      remainingTextLen: command.remainingText.length,
    });

    if (!command.remainingText) {
      return false;
    }

    remoteLog('placeholder', 'dbg_voice_send', {
      text: command.remainingText.substring(0, 50),
      source,
      targetSessionKey: command.targetSessionKey,
      voiceCommandKind: command.kind,
      matchedAlias: command.kind === 'address' || command.kind === 'ambiguous'
        ? command.matchedAlias
        : undefined,
      matchedBy: command.kind === 'address' ? command.matchedBy : undefined,
      candidateSessionKeys: command.kind === 'ambiguous'
        ? command.candidateSessionKeys
        : undefined,
      ...extraLog,
    });
    playAudioCue('speechEndThinking', speakerMutedRef.current, isActivatedRef.current);

    void sendUserChatMessage({
      sessionKey: command.targetSessionKey,
      displayMessage: command.remainingText,
      gatewayId: activeGateway.id,
      gatewayName: activeGateway.name,
      source: 'voice',
      extraLog: {
        targetSessionKey: command.targetSessionKey,
        voiceCommandKind: command.kind,
      },
    }).catch((err) => {
      console.error('[App] Failed to send voice transcript:', err);
      playAudioCue('error', speakerMutedRef.current, isActivatedRef.current);
    });

    return true;
  }

  const submitVoiceTranscript = useCallback((
    transcript: string,
    source: 'turn_arbiter' | 'legacy' | 'turn_cancel_fallback',
    extraLog?: Record<string, unknown>,
  ): boolean => {
    if (maybeHandleVoiceControlCommand(transcript, source, extraLog)) {
      return true;
    }
    if (isMicMutedRef.current) {
      return false;
    }

    const command = parseVoiceCommand({
      transcript,
      activeSessionKey: activeVoiceSessionKey,
      agents: voiceRouteAgents,
      sessions: voiceRouteSessions,
    });

    return sendResolvedVoiceTranscript(command, source, extraLog);
  }, [
    activeVoiceSessionKey,
    voiceRouteAgents,
    voiceRouteSessions,
    sendResolvedVoiceTranscript,
    maybeHandleVoiceControlCommand,
  ]);

  // ── Turn Arbiter: commit handler (fires chat.send with full accumulated turn) ──
  const handleTurnCommit = useCallback((fullTranscript: string) => {
    const commandContextLog = {
      turnProtocolEnabled: turnProtocolRef.current?.enabled ?? false,
      bargeInState: bargeInRef.current?.state ?? 'none',
    };

    if (maybeHandleVoiceControlCommand(fullTranscript, 'turn_arbiter', commandContextLog)) {
      setMessagesRef.current((prev) => prev.filter((m) => m.id !== draftBubbleIdRef.current));
      finalizeCommittedVoiceTurn();
      return;
    }

    if (!wsClient || !isConnected || !activeGateway) {
      voiceCommandTurnRef.current?.reset();
      return;
    }

    if (isMicMutedRef.current) {
      setMessagesRef.current((prev) => prev.filter((m) => m.id !== draftBubbleIdRef.current));
      remoteLog('voice', 'command_discarded_command_only', {
        source: 'turn_arbiter',
        transcriptLen: fullTranscript.trim().length,
        ...commandContextLog,
      });
      finalizeCommittedVoiceTurn();
      return;
    }

    // Remove the draft bubble
    setMessagesRef.current((prev) => prev.filter((m) => m.id !== draftBubbleIdRef.current));

    const command = voiceCommandTurnRef.current?.resolveSubmitCommand({
      transcript: fullTranscript,
      activeSessionKey: activeVoiceSessionKey,
      agents: voiceRouteAgents,
      sessions: voiceRouteSessions,
    }) ?? {
      kind: 'none' as const,
      originalText: fullTranscript.trim(),
      remainingText: fullTranscript.trim(),
      targetSessionKey: activeVoiceSessionKey,
      shouldSwitchSession: false,
    };

    sendResolvedVoiceTranscript(command, 'turn_arbiter', {
      ...commandContextLog,
      resolvedRouteKind: command.kind,
    });
    finalizeCommittedVoiceTurn();
  }, [
    wsClient,
    isConnected,
    activeGateway,
    activeVoiceSessionKey,
    voiceRouteAgents,
    voiceRouteSessions,
    sendResolvedVoiceTranscript,
    maybeHandleVoiceControlCommand,
    finalizeCommittedVoiceTurn,
  ]);

  // ── Turn Arbiter: cancel handler (misfire cleanup vs explicit abort) ──
  const handleTurnCancel = useCallback((meta: TurnCancelMeta) => {
    const shouldFinalizeBufferedCommand = !meta.hadDraft && (
      meta.reason === 'speech_end_no_segment' || meta.reason === 'empty_no_draft'
    );
    const bufferedCommand = shouldFinalizeBufferedCommand
      ? voiceCommandTurnRef.current?.finalizeBufferedCommand({
          activeSessionKey: activeVoiceSessionKey,
          agents: voiceRouteAgents,
          sessions: voiceRouteSessions,
        }) ?? null
      : null;

    voiceCommandTurnRef.current?.reset();

    // Remove the draft bubble
    setMessagesRef.current((prev) => prev.filter((m) => m.id !== draftBubbleIdRef.current));

    // FSM: mark turn as cancelled (returns input to idle, releases TTS deferral)
    send({ type: 'TURN_CANCELLED' });

    if (bufferedCommand) {
      sendResolvedVoiceTranscript(bufferedCommand, 'turn_cancel_fallback', {
        turnCancelReason: meta.reason,
        turnCancelHadDraft: meta.hadDraft,
      });
    }

    const actor = getFSMActor();
    const deferred = actor.getSnapshot().context.deferredTtsTexts;
    if (deferred.length === 0) {
      maybeStartPendingSessionReadoutRef.current();
      return;
    }

    // Always clear the deferred queue from FSM state. Misfire cleanup resumes it;
    // explicit user-driven cancels intentionally discard it.
    actor.send({ type: 'TTS_DEFERRED_FLUSH' });

    if (!meta.shouldFlushDeferredTts) {
      remoteLog('tts', 'drop_deferred_turn_cancel', {
        count: deferred.length,
        reason: meta.reason,
        hadDraft: meta.hadDraft,
      });
      maybeStartPendingSessionReadoutRef.current();
      return;
    }

    remoteLog('tts', 'flush_deferred_turn_cancel', {
      count: deferred.length,
      reason: meta.reason,
      hadDraft: meta.hadDraft,
    });

    const textsToSpeak = [...deferred];
    setTimeout(() => {
      for (const t of textsToSpeak) {
        voiceRef.current.speak(t);
      }
      maybeStartPendingSessionReadoutRef.current();
    }, 0);
  }, [
    activeVoiceSessionKey,
    voiceRouteAgents,
    voiceRouteSessions,
    sendResolvedVoiceTranscript,
    send,
  ]);

  // ── Turn Arbiter: draft update handler (show ephemeral bubble) ──
  const handleDraftUpdate = useCallback((draftText: string) => {
    if (!activeGateway) return;
    setMessagesRef.current((prev) => {
      const existingIdx = prev.findIndex((m) => m.id === draftBubbleIdRef.current);
      const draftMsg = {
        id: draftBubbleIdRef.current,
        role: 'user' as const,
        content: draftText,
        timestamp: new Date().toISOString(),
        isHistory: false,
        isStreaming: false,
        ttsStatus: 'none' as const,
        isDraft: true,
        gatewayId: activeGateway.id,
        gatewayName: activeGateway.name,
      };
      if (existingIdx >= 0) {
        const updated = [...prev];
        updated[existingIdx] = draftMsg;
        return updated;
      }
      return [...prev, draftMsg];
    });
  }, [activeGateway]);

  // ── Turn Protocol Manager (singleton — lives for the component lifetime) ──
  const turnProtocolRef = useRef<TurnProtocolManager | null>(null);
  if (!turnProtocolRef.current) {
    turnProtocolRef.current = new TurnProtocolManager();
  }

  // ── Barge-In Manager ──
  const bargeInRef = useRef<BargeInManager | null>(null);
  // Track previous isTTSPlaying so we only fire on the false→true transition.
  const prevTTSPlayingRef = useRef(false);
  // Refs so the TTS effect can read current values without taking them as deps
  // (avoids re-running the effect when isSpeaking/voiceInterrupt change mid-TTS).
  const isSpeakingRef = useRef(false);
  const voiceInterruptRef = useRef(false);

  // ── Turn Arbiter instance ──
  const turnArbiterRef = useRef<TurnArbiter | null>(null);

  // Keep arbiter callbacks current via refs
  const handleTurnCommitRef = useRef(handleTurnCommit);
  handleTurnCommitRef.current = handleTurnCommit;
  const handleTurnCancelRef = useRef(handleTurnCancel);
  handleTurnCancelRef.current = handleTurnCancel;
  const handleDraftUpdateRef = useRef(handleDraftUpdate);
  handleDraftUpdateRef.current = handleDraftUpdate;

  // Lazily create the arbiter on first use; callbacks are forwarded via refs
  // so the arbiter instance survives across re-renders without recreation.
  const getOrCreateArbiter = useCallback((): TurnArbiter => {
    if (!turnArbiterRef.current) {
      turnArbiterRef.current = new TurnArbiter({
        onCommit: (text) => handleTurnCommitRef.current(text),
        onCancel: (meta) => handleTurnCancelRef.current(meta),
        onDraftUpdate: (text) => handleDraftUpdateRef.current(text),
      });
      // Wire protocol manager if available
      if (turnProtocolRef.current) {
        turnArbiterRef.current.setProtocol(turnProtocolRef.current);
      }
    }
    return turnArbiterRef.current;
  }, []);

  // Lazily create the barge-in manager with callbacks that route through refs
  const getOrCreateBargeIn = useCallback((): BargeInManager => {
    if (!bargeInRef.current) {
      bargeInRef.current = new BargeInManager({
        onDuck: () => {
          remoteLog('bargeIn', 'callback_duck', {});
          duckTtsGain(150);
        },
        onUnduck: () => {
          remoteLog('bargeIn', 'callback_unduck', {});
          restoreTtsGain(200);
        },
        onPromote: (reason: string) => {
          remoteLog('bargeIn', 'callback_promote', { reason, runId: currentRunIdRef.current });
          // Real interrupt confirmed — stop TTS, suppress future TTS for this run,
          // abort the active request
          voiceRef.current.stopSpeaking();
          ttsSuppressionRef.current = currentRunIdRef.current;
          sendRef.current({ type: 'TTS_CANCEL', reason: `bargein-${reason}` });

          // Abort active run on the gateway
          const client = wsClientRef.current;
          if (client && client.isOpen) {
            abortChat(client, activeSessionKeyRef.current).catch((err) => {
              remoteLog('bargeIn', 'abort_error', { error: String(err) });
            });
          }
        },
      });
    }
    return bargeInRef.current;
  }, []);

  function applyVoiceTurnSegmentDecision(
    arbiter: TurnArbiter,
    decision: VoiceTurnSegmentDecision,
  ): void {
    if (decision.action === 'hold') {
      return;
    }

    turnProtocolRef.current?.setSessionKey(decision.route.targetSessionKey);

    if (decision.shouldSwitchSession) {
      const canSwitch = prepareVoiceSessionSwitch(decision.route.targetSessionKey, {
        announceSuccess: !decision.text,
      });
      if (!canSwitch) {
        return;
      }

      if (!decision.text) {
        switchOnlyVoiceCommandSessionKeyRef.current = decision.route.targetSessionKey;
      }

      const activeGatewayId = activeGatewayRef.current?.id ?? activeGateway?.id ?? null;
      skipNextSessionReadoutRef.current = decision.text && activeGatewayId
        ? {
            gatewayId: activeGatewayId,
            sessionKey: decision.route.targetSessionKey,
            loadId: null,
          }
        : null;
      switchVoiceSession(decision.route.targetSessionKey);
    }

    if (decision.action !== 'emit' || !decision.text) {
      if (!decision.shouldSwitchSession && !decision.text && decision.route.kind === 'address') {
        switchOnlyVoiceCommandSessionKeyRef.current = decision.route.targetSessionKey;
        clearPendingVoiceSwitchAnnouncement();
        queueStandaloneSessionAnnouncement(
          decision.route.targetSessionKey,
          formatVoiceSessionAnnouncement(decision.route.targetSessionKey, 'already_on'),
        );
      }
      return;
    }

    if (switchOnlyVoiceCommandSessionKeyRef.current === decision.route.targetSessionKey) {
      cancelPendingSwitchOnlyVoiceFeedback(decision.route.targetSessionKey);
      const pendingSwitchLoad = pendingSessionSwitchLoadRef.current;
      if (pendingSwitchLoad?.sessionKey === decision.route.targetSessionKey) {
        skipNextSessionReadoutRef.current = {
          gatewayId: pendingSwitchLoad.gatewayId,
          sessionKey: decision.route.targetSessionKey,
          loadId: pendingSwitchLoad.loadId,
        };
      }
    }

    arbiter.addSegment(decision.text);

    // Ensure FSM is in accumulating state if not already
    const actor = getFSMActor();
    const inputState = (actor.getSnapshot().value as Record<string, string>).input;
    if (inputState === 'idle' && arbiter.isAccumulating) {
      actor.send({ type: 'TURN_ACCUMULATING' });
    }
  }

  // ── Voice segment handler (turn arbiter mode) ──
  const handleVoiceSegment = useCallback((segment: VoiceSegmentInput) => {
    const text = typeof segment === 'string' ? segment : segment.text;
    const gateRejected = typeof segment !== 'string' && segment.gateRejected === true;
    const trimmedText = text.trim();
    const hasContent = trimmedText.length > 0;
    const bypassCommand = gateRejected && hasContent
      ? resolveVoiceLockSwitchOnlyCommand({
          transcript: trimmedText,
          activeSessionKey: activeVoiceSessionKey,
          agents: voiceRouteAgents,
          sessions: voiceRouteSessions,
        })
      : null;
    const bargeInHasContent = gateRejected ? !!bypassCommand : hasContent;

    // Notify barge-in manager of STT result (for duck→promote/resume decision)
    if (bargeInRef.current?.isDucked) {
      bargeInRef.current.sttResult(bargeInHasContent);
      remoteLog('bargeIn', 'stt_result_during_duck', {
        hasContent: bargeInHasContent,
        text: text.substring(0, 40),
        gateRejected,
      });
    }

    const arbiter = getOrCreateArbiter();

    if (isMicMutedRef.current) {
      arbiter.addSegment(hasContent ? trimmedText : '');

      if (hasContent) {
        const actor = getFSMActor();
        const inputState = (actor.getSnapshot().value as Record<string, string>).input;
        if (inputState === 'idle' && arbiter.isAccumulating) {
          actor.send({ type: 'TURN_ACCUMULATING' });
        }
      }
      return;
    }

    if (gateRejected) {
      if (!bypassCommand) {
        arbiter.addSegment('');
        return;
      }

      applyVoiceTurnSegmentDecision(arbiter, {
        action: 'switch_only',
        text: '',
        route: {
          kind: 'address',
          targetSessionKey: bypassCommand.targetSessionKey,
          matchedAlias: bypassCommand.matchedAlias,
          matchedBy: bypassCommand.matchedBy,
        },
        shouldSwitchSession: bypassCommand.targetSessionKey !== activeVoiceSessionKey,
      });
      return;
    }

    const routedSegment = routeVoiceTurnSegment({
      controller: voiceCommandTurnRef.current,
      segment: text,
      activeSessionKey: activeVoiceSessionKey,
      agents: voiceRouteAgents,
      sessions: voiceRouteSessions,
    });

    if (routedSegment.kind === 'empty') {
      arbiter.addSegment('');
      return;
    }

    applyVoiceTurnSegmentDecision(arbiter, routedSegment.decision);
  }, [
    activeVoiceSessionKey,
    applyVoiceTurnSegmentDecision,
    getOrCreateArbiter,
    voiceRouteAgents,
    voiceRouteSessions,
  ]);

  const getOrCreateVoiceSpeechTurnArbiter = useCallback(() => {
    const arbiter = getOrCreateArbiter();

    return {
      onSpeechStart: () => {
        arbiter.onSpeechStart();
      },
      onSpeechEnd: () => {
        // Keep buffered alias prefixes alive across normal intra-turn pauses.
        // They only finalize if a later segment resolves them or the turn truly
        // ends/cancels and handleTurnCancel intentionally degrades them.
        arbiter.onSpeechEnd();
      },
    };
  }, [getOrCreateArbiter]);

  // Legacy transcript handler (kept as fallback, but not wired in Phase 1)
  const handleVoiceTranscript = useCallback((text: string) => {
    submitVoiceTranscript(text, 'legacy');
  }, [submitVoiceTranscript]);

  // ── Voice Lock (speaker verification) ──
  const voiceLock = useVoiceLock();

  const lastTtsErrorRef = useRef<{ message: string; ts: number } | null>(null);
  const handleTtsError = useCallback((message: string) => {
    const nowMs = Date.now();
    const last = lastTtsErrorRef.current;
    if (last?.message === message && nowMs - last.ts < 15_000) {
      return;
    }
    lastTtsErrorRef.current = { message, ts: nowMs };
    addMessage({
      id: `system-tts-${nowMs}`,
      role: 'system',
      content: `⚠️ ${message}`,
      timestamp: new Date(nowMs).toISOString(),
      isHistory: false,
      isStreaming: false,
      ttsStatus: 'none',
    });
  }, [addMessage]);

  // ── Voice (targets focused gateway) — wired with turn arbiter ──
  const voice = useVoice({
    wsClient,
    sessionKey: sessionManager.activeSessionKey,
    onTranscript: handleVoiceTranscript,
    onSegment: handleVoiceSegment,
    onSpeechAudio: voiceLock.onVADSpeechEnd,
    shouldProcessAudio: voiceLock.scoreAudioForGate,
    onTtsError: handleTtsError,
  });

  // ── Privacy Mode ──
  const { isActive: privacyActive, toggle: togglePrivacy, dismiss: dismissPrivacy } = usePrivacyMode();

  // ── Settings & Session Panel ──
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sessionPanelOpen, setSessionPanelOpen] = useState(false);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [showAddGateway, setShowAddGateway] = useState(false);

  // ── Screen Routing ──
  const hasGateways = gateways.length > 0;
  const [screen, setScreen] = useState<Screen>(hasGateways ? 'app' : 'setup');

  // ── Pairing State ──
  const [pairingState, setPairingState] = useState<PairingState | null>(null);
  const [pairingGateway, setPairingGateway] = useState<GatewayConfig | null>(null);

  // ── Refs ──
  const streamingHandlersRef = useRef<Map<string, StreamingHandler>>(new Map());
  const sentenceBufferRef = useRef('');
  const deltaAccumulatedRef = useRef('');
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentRunIdRef = useRef<string | null>(null);
  const historyLoadedRef = useRef<Set<string>>(new Set());

  // Refs for values needed in streaming handler closures
  const speakerMutedRef = useRef(isSpeakerMuted);
  speakerMutedRef.current = isSpeakerMuted;
  const isActivatedRef = useRef(isActivated);
  isActivatedRef.current = isActivated;
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const sendRef = useRef(send);
  sendRef.current = send;
  const patchPlaceholderRunIdRef = useRef(patchPlaceholderRunId);
  patchPlaceholderRunIdRef.current = patchPlaceholderRunId;
  const updateStreamingRef = useRef(updateStreaming);
  updateStreamingRef.current = updateStreaming;
  const finalizeStreamingRef = useRef(finalizeStreaming);
  finalizeStreamingRef.current = finalizeStreaming;
  const abortStreamingRef = useRef(abortStreaming);
  abortStreamingRef.current = abortStreaming;
  const errorStreamingRef = useRef(errorStreaming);
  errorStreamingRef.current = errorStreaming;
  const wsClientRef = useRef(wsClient);
  wsClientRef.current = wsClient;
  const activeGatewayRef = useRef(activeGateway);
  activeGatewayRef.current = activeGateway;
  const activeGatewayIdRef = useRef(activeGateway?.id ?? null);
  activeGatewayIdRef.current = activeGateway?.id ?? null;
  const activeSessionKeyRef = useRef(sessionManager.activeSessionKey);
  activeSessionKeyRef.current = sessionManager.activeSessionKey;
  const sessionManagerRef = useRef(sessionManager);
  sessionManagerRef.current = sessionManager;
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const setMessagesRef = useRef(chat.setMessages);
  setMessagesRef.current = chat.setMessages;
  const gatewaysRef = useRef(gateways);
  gatewaysRef.current = gateways;
  const sessionReadCursorRef = useRef<Map<string, SessionReadCursor>>(new Map());
  const sessionSwitchLoadRef = useRef(0);
  const skipNextSessionReadoutRef = useRef<{
    gatewayId: string;
    sessionKey: string;
    loadId: number | null;
  } | null>(null);
  const pendingSessionReadoutRef = useRef<PendingSessionReadout | null>(null);
  const activeSessionReadoutRef = useRef<{ cursorKey: string; runId: number; pending: PendingSessionReadout } | null>(null);
  const sessionReadoutRunRef = useRef(0);
  const pendingSessionSwitchLoadRef = useRef<{
    gatewayId: string;
    sessionKey: string;
    loadId: number;
  } | null>(null);
  const pendingVoiceSwitchAnnouncementRef = useRef<PendingVoiceSwitchAnnouncement | null>(null);
  const pendingVoiceSwitchFailureTimerRef = useRef<number | null>(null);
  const switchOnlyVoiceCommandSessionKeyRef = useRef<string | null>(null);

  const clearPendingVoiceSwitchAnnouncement = useCallback(() => {
    if (pendingVoiceSwitchFailureTimerRef.current !== null) {
      window.clearTimeout(pendingVoiceSwitchFailureTimerRef.current);
      pendingVoiceSwitchFailureTimerRef.current = null;
    }
    pendingVoiceSwitchAnnouncementRef.current = null;
  }, []);

  const formatVoiceSessionAnnouncement = useCallback((
    sessionKey: string,
    kind: 'switched' | 'already_on' | 'failed',
  ): string => {
    const label = buildVoiceSessionLabel(findManagedSession(sessionKey), sessionKey, voiceRouteAgents);
    switch (kind) {
      case 'switched':
        return `Switched to ${label}.`;
      case 'already_on':
        return `Already on ${label}.`;
      case 'failed':
        return `Could not switch to ${label}.`;
    }
  }, [findManagedSession, voiceRouteAgents]);

  const queueStandaloneSessionAnnouncement = useCallback((sessionKey: string, text: string): void => {
    const gateway = activeGatewayRef.current;
    if (!gateway || !isActivatedRef.current || speakerMutedRef.current) {
      return;
    }

    pendingSessionReadoutRef.current = {
      cursorKey: buildSessionReadCursorKey(gateway.id, sessionKey),
      gatewayId: gateway.id,
      sessionKey,
      unreadMessages: [],
      speechQueue: [text],
      trackedSpeechOffset: 0,
    };
  }, []);

  const prepareVoiceSessionSwitch = useCallback((
    sessionKey: string,
    options?: { announceSuccess?: boolean },
  ): boolean => {
    const announceSuccess = options?.announceSuccess ?? false;

    if (!findManagedSession(sessionKey)) {
      clearPendingVoiceSwitchAnnouncement();
      switchOnlyVoiceCommandSessionKeyRef.current = null;
      queueStandaloneSessionAnnouncement(
        activeSessionKeyRef.current,
        formatVoiceSessionAnnouncement(sessionKey, 'failed'),
      );
      return false;
    }

    if (!announceSuccess) {
      clearPendingVoiceSwitchAnnouncement();
      switchOnlyVoiceCommandSessionKeyRef.current = null;
      return true;
    }

    const gateway = activeGatewayRef.current;
    if (!gateway) {
      clearPendingVoiceSwitchAnnouncement();
      switchOnlyVoiceCommandSessionKeyRef.current = null;
      return false;
    }

    clearPendingVoiceSwitchAnnouncement();
    pendingVoiceSwitchAnnouncementRef.current = {
      gatewayId: gateway.id,
      sessionKey,
      text: formatVoiceSessionAnnouncement(sessionKey, 'switched'),
    };

    pendingVoiceSwitchFailureTimerRef.current = window.setTimeout(() => {
      const pending = pendingVoiceSwitchAnnouncementRef.current;
      if (!pending || pending.gatewayId !== gateway.id || pending.sessionKey !== sessionKey) {
        return;
      }

      clearPendingVoiceSwitchAnnouncement();
      switchOnlyVoiceCommandSessionKeyRef.current = null;
      queueStandaloneSessionAnnouncement(
        activeSessionKeyRef.current,
        formatVoiceSessionAnnouncement(sessionKey, 'failed'),
      );
      maybeStartPendingSessionReadoutRef.current();
    }, 500);

    return true;
  }, [
    clearPendingVoiceSwitchAnnouncement,
    findManagedSession,
    formatVoiceSessionAnnouncement,
    queueStandaloneSessionAnnouncement,
  ]);

  const cancelPendingSwitchOnlyVoiceFeedback = useCallback((sessionKey: string) => {
    clearPendingVoiceSwitchAnnouncement();

    const activeReadout = activeSessionReadoutRef.current;
    if (activeReadout?.pending.sessionKey === sessionKey) {
      sessionReadoutRunRef.current += 1;
      const handledCursor = buildSessionReadCursor(activeReadout.pending.unreadMessages);
      if (handledCursor) {
        sessionReadCursorRef.current.set(activeReadout.cursorKey, handledCursor);
      }
      voiceRef.current.stopSpeaking();
      activeSessionReadoutRef.current = null;
    }

    const pendingReadout = pendingSessionReadoutRef.current;
    if (pendingReadout?.sessionKey === sessionKey) {
      const handledCursor = buildSessionReadCursor(pendingReadout.unreadMessages);
      if (handledCursor) {
        sessionReadCursorRef.current.set(pendingReadout.cursorKey, handledCursor);
      }
      pendingSessionReadoutRef.current = null;
    }

    switchOnlyVoiceCommandSessionKeyRef.current = null;
  }, [clearPendingVoiceSwitchAnnouncement]);

  useEffect(() => () => {
    clearPendingVoiceSwitchAnnouncement();
  }, [clearPendingVoiceSwitchAnnouncement]);

  // Track which session each runId belongs to (for TTS filtering)
  const runSessionMapRef = useRef<Map<string, string>>(new Map());

  // Ref for subscribed sessions (used in streaming handler closures)
  const subscribedSessionsRef = useRef(agentTicker.subscribedSessions);
  subscribedSessionsRef.current = agentTicker.subscribedSessions;

  // Fast-lane subscription ref: populated synchronously in streaming handlers
  // to bridge the gap between calling subscribe() and React state commit.
  const immediateSubscribedRef = useRef<Set<string>>(new Set());

  // Ref to agentTicker.subscribe for use in wireStreamingHandler closure
  const agentTickerSubscribeRef = useRef(agentTicker.subscribe);
  agentTickerSubscribeRef.current = agentTicker.subscribe;

  // Refs for gateway event handlers (used in wireStreamingHandler closure)
  const gatewayEventsRef = useRef(gatewayEvents);
  gatewayEventsRef.current = gatewayEvents;

  // Clean up fast-lane entries once the official subscribedSessions catches up
  useEffect(() => {
    for (const key of immediateSubscribedRef.current) {
      if (agentTicker.subscribedSessions.has(key)) {
        immediateSubscribedRef.current.delete(key);
      }
    }
  }, [agentTicker.subscribedSessions]);

  // When depth filter is reduced/turned off, clear all fast-lane entries so
  // unsubscribed sessions don't leak through the isSub() check.
  useEffect(() => {
    const handleDepthChange = (e: Event) => {
      const newDepth = parseInt((e as CustomEvent).detail ?? '0', 10);
      if (newDepth === 0) {
        immediateSubscribedRef.current.clear();
      } else {
        // Remove fast-lane entries for sessions that are too deep
        for (const key of immediateSubscribedRef.current) {
          const sessionDepth = (key.match(/:subagent:/g) || []).length;
          if (sessionDepth > newDepth) {
            immediateSubscribedRef.current.delete(key);
          }
        }
      }
    };
    window.addEventListener('subagent-depth-change', handleDepthChange);
    return () => window.removeEventListener('subagent-depth-change', handleDepthChange);
  }, []);

  // Track which session key belongs to which label (for agent badges)
  const sessionLabelMapRef = useRef<Map<string, string>>(new Map());

  // Main session key ref for back-to-main feature
  const mainSessionKeyRef = useRef(getInitialSessionKey());

  // Track last delta timestamp per runId for sub-agent split mode
  const lastSubagentDeltaTimeRef = useRef<Map<string, number>>(new Map());

  // Track last delta timestamp per runId for active session gap detection
  const lastDeltaTimeRef = useRef<Map<string, number>>(new Map());

  // Suppress TTS for a specific runId after explicit user interrupt (orb tap or voice interrupt)
  const ttsSuppressionRef = useRef<string | null>(null);

  // ── Thinking state tracking (per runId) ──
  const thinkingStateRef = useRef<Map<string, { startTime: number; text: string }>>(new Map());

  // Track runIds that have at least one tool in 'running' status.
  // Used to efficiently mark running tools as 'done' on the first delta.
  const runIdsWithActiveToolRef = useRef<Set<string>>(new Set());

  /** Mark all running tool calls for a run as 'done' */
  const markToolsDone = useCallback((runId: string) => {
    if (!runIdsWithActiveToolRef.current.has(runId)) return;
    runIdsWithActiveToolRef.current.delete(runId);
    setMessagesRef.current((prev) => markAllToolCallsDone(prev, runId));
  }, []);

  const isSessionReadoutPlaybackBlocked = useCallback((): boolean => {
    const snapshot = getFSMActor().getSnapshot();
    const inputState = (snapshot.value as Record<string, string>).input;
    if (snapshot.context.ttsAutoPlayDeferred) {
      return true;
    }
    if (inputState === 'speechDetected' || inputState === 'accumulating') {
      return true;
    }
    return turnArbiterRef.current?.hasPendingTurn ?? false;
  }, []);

  const maybeStartPendingSessionReadout = useCallback(() => {
    const pending = pendingSessionReadoutRef.current;
    if (!pending || activeSessionReadoutRef.current) {
      return;
    }

    const hasUnreadMessages = pending.unreadMessages.length > 0;

    if (!readUnheardMessagesOnSessionSwitch && hasUnreadMessages && pending.speechQueue.length === 0) {
      const handledCursor = buildSessionReadCursor(pending.unreadMessages);
      if (handledCursor) {
        sessionReadCursorRef.current.set(pending.cursorKey, handledCursor);
      }
      pendingSessionReadoutRef.current = null;
      return;
    }

    if (!isActivatedRef.current || speakerMutedRef.current) {
      return;
    }
    if (isSessionReadoutPlaybackBlocked()) {
      return;
    }

    if (switchOnlyVoiceCommandSessionKeyRef.current === pending.sessionKey) {
      switchOnlyVoiceCommandSessionKeyRef.current = null;
    }

    pendingSessionReadoutRef.current = null;
    const runId = ++sessionReadoutRunRef.current;
    activeSessionReadoutRef.current = { cursorKey: pending.cursorKey, runId, pending };

    void (async () => {
      try {
        if (pending.speechQueue.length === 0) {
          const handledCursor = buildSessionReadCursor(pending.unreadMessages);
          if (handledCursor) {
            sessionReadCursorRef.current.set(pending.cursorKey, handledCursor);
          }
          return;
        }

        let handledCount = 0;
        const result = await voiceRef.current.speakTracked(pending.speechQueue, {
          onChunkComplete: (chunkIndex) => {
            const messageIndex = chunkIndex - pending.trackedSpeechOffset;
            const handledMessage = messageIndex >= 0
              ? pending.unreadMessages[messageIndex]
              : undefined;
            const handledCursor = buildSessionReadCursorFromMessage(handledMessage);
            if (handledCursor) {
              sessionReadCursorRef.current.set(pending.cursorKey, handledCursor);
            }
            if (handledMessage) {
              handledCount = Math.max(handledCount, messageIndex + 1);
            }
          },
        });
        if (activeSessionReadoutRef.current?.runId !== runId) {
          return;
        }

        if (result !== 'completed') {
          // Any interruption (mute, barge-in, cancel) marks ALL remaining
          // messages as heard. The user interrupted intentionally — never
          // replay them.
          const dismissedCursor = buildSessionReadCursor(pending.unreadMessages);
          if (dismissedCursor) {
            sessionReadCursorRef.current.set(pending.cursorKey, dismissedCursor);
          }
          pendingSessionReadoutRef.current = null;
          remoteLog('voice', 'session_switch_readout_dismissed', {
            gatewayId: pending.gatewayId,
            sessionKey: pending.sessionKey,
            dismissedCount: pending.unreadMessages.length - handledCount,
            reason: speakerMutedRef.current ? 'muted' : 'interrupted',
          });
          return;
        }

        const handledCursor = buildSessionReadCursor(pending.unreadMessages);
        if (handledCursor) {
          sessionReadCursorRef.current.set(pending.cursorKey, handledCursor);
        }

        remoteLog('voice', 'session_switch_readout_complete', {
          gatewayId: pending.gatewayId,
          sessionKey: pending.sessionKey,
          handledCount: pending.unreadMessages.length,
        });
      } finally {
        if (activeSessionReadoutRef.current?.runId === runId) {
          activeSessionReadoutRef.current = null;
        }
      }
    })();
  }, [isSessionReadoutPlaybackBlocked, readUnheardMessagesOnSessionSwitch]);

  maybeStartPendingSessionReadoutRef.current = maybeStartPendingSessionReadout;

  const loadSessionReadoutHistory = useCallback(async (
    client: WSClient,
    gatewayId: string,
    gatewayName: string,
    sessionKey: string,
    cursor: SessionReadCursor | null,
  ): Promise<ChatMessage[]> => {
    let before: string | undefined;
    const pageBatches: Awaited<ReturnType<typeof fetchChatHistory>>[] = [];

    while (true) {
      const rawPage = await fetchChatHistory(
        client,
        sessionKey,
        SESSION_READOUT_HISTORY_PAGE_SIZE,
        before,
      );

      if (rawPage.length === 0) {
        break;
      }

      pageBatches.unshift(rawPage);

      const historyMessages = pageBatches.flat().flatMap((message, index) => {
        const chatMessage = historyMessageToChatMessage(message, index, gatewayId, gatewayName);
        return chatMessage ? [chatMessage] : [];
      });

      const oldestCursor = rawPage[0]?.ts;
      const exhausted = rawPage.length < SESSION_READOUT_HISTORY_PAGE_SIZE || !oldestCursor;
      if (!cursor || hasSessionReadCursorMatch(historyMessages, cursor) || exhausted) {
        return historyMessages;
      }

      if (before === oldestCursor) {
        return historyMessages;
      }
      before = oldestCursor;
    }

    return [];
  }, []);

  // ── TTS Debug (per-message) ──
  const ttsDebugOrderRef = useRef(0);

  /** Append a TTS debug sentence to the message matching the given runId */
  const appendTtsDebugSentence = useCallback((runId: string, sentence: TtsDebugSentence) => {
    setMessagesRef.current((prev) => {
      const idx = prev.findIndex((m) => m.runId === runId && m.role === 'assistant');
      if (idx < 0) return prev;
      const updated = [...prev];
      const msg = updated[idx];
      updated[idx] = {
        ...msg,
        ttsDebugSentences: [...(msg.ttsDebugSentences ?? []), sentence],
      };
      return updated;
    });
  }, []);

  // Update session switch handler ref now that historyLoadedRef is available
  sessionSwitchRef.current = (_gwId: string, sessionKey: string) => {
    const gateway = activeGatewayRef.current;
    if (!gateway) return;

    const client = getClient(gateway.id);
    if (!client) return;

    const sourceSessionKey = activeSessionKeyRef.current;
    const sourceCursorKey = buildSessionReadCursorKey(gateway.id, sourceSessionKey);
    const sourceHasUnfinishedReadout =
      activeSessionReadoutRef.current?.cursorKey === sourceCursorKey
      || pendingSessionReadoutRef.current?.cursorKey === sourceCursorKey;

    if (!sourceHasUnfinishedReadout) {
      const sourceCursor = buildSessionReadCursor(
        collectVisibleActiveSessionMessages(messagesRef.current, gateway.id),
      );
      if (sourceCursor) {
        sessionReadCursorRef.current.set(sourceCursorKey, sourceCursor);
      } else {
        sessionReadCursorRef.current.delete(sourceCursorKey);
      }
    }

    if (activeSessionReadoutRef.current || pendingSessionReadoutRef.current) {
      sessionReadoutRunRef.current += 1;
      // Advance cursor for any unread messages in the interrupted readout so they
      // are not replayed when the user returns to the source session.
      const interruptedReadout =
        activeSessionReadoutRef.current?.pending ?? pendingSessionReadoutRef.current;
      if (interruptedReadout?.cursorKey === sourceCursorKey) {
        const finalCursor = buildSessionReadCursor(interruptedReadout.unreadMessages);
        if (finalCursor) {
          sessionReadCursorRef.current.set(sourceCursorKey, finalCursor);
        }
      }
      if (activeSessionReadoutRef.current) {
        voiceRef.current.stopSpeaking();
      }
      activeSessionReadoutRef.current = null;
      pendingSessionReadoutRef.current = null;
    }

    const destinationCursorKey = buildSessionReadCursorKey(gateway.id, sessionKey);
    const previousDestinationCursor = sessionReadCursorRef.current.get(destinationCursorKey) ?? null;
    const loadId = ++sessionSwitchLoadRef.current;
    pendingSessionSwitchLoadRef.current = {
      gatewayId: gateway.id,
      sessionKey,
      loadId,
    };

    const pendingSkipReadout = skipNextSessionReadoutRef.current;
    if (pendingSkipReadout?.gatewayId === gateway.id) {
      if (pendingSkipReadout.loadId !== null && pendingSkipReadout.loadId !== loadId) {
        skipNextSessionReadoutRef.current = null;
      } else if (
        pendingSkipReadout.sessionKey === sessionKey
        && pendingSkipReadout.loadId === null
      ) {
        skipNextSessionReadoutRef.current = {
          ...pendingSkipReadout,
          loadId,
        };
      }
    }

    const pendingVoiceSwitchAnnouncement =
      pendingVoiceSwitchAnnouncementRef.current?.gatewayId === gateway.id
      && pendingVoiceSwitchAnnouncementRef.current?.sessionKey === sessionKey
        ? pendingVoiceSwitchAnnouncementRef.current
        : null;

    if (pendingVoiceSwitchAnnouncement) {
      clearPendingVoiceSwitchAnnouncement();
    }

    chat.clearMessages();
    historyLoadedRef.current.delete(gateway.id);
    void loadSessionReadoutHistory(
      client,
      gateway.id,
      gateway.name,
      sessionKey,
      previousDestinationCursor,
    )
      .then((historyMessages) => {
        if (loadId !== sessionSwitchLoadRef.current || activeGatewayIdRef.current !== gateway.id) {
          return;
        }

        const readoutPlan = buildSessionReadoutPlan(historyMessages, previousDestinationCursor);
        const skipReadout = skipNextSessionReadoutRef.current;
        const skipReadoutForAttachedMessage =
          skipReadout?.gatewayId === gateway.id
          && skipReadout.sessionKey === sessionKey
          && skipReadout.loadId === loadId;
        if (skipReadoutForAttachedMessage) {
          skipNextSessionReadoutRef.current = null;
        }
        const isStillSwitchOnlyVoiceCommand = switchOnlyVoiceCommandSessionKeyRef.current === sessionKey;
        const shouldSpeakSwitchConfirmation =
          !!pendingVoiceSwitchAnnouncement
          && isStillSwitchOnlyVoiceCommand
          && isActivatedRef.current
          && !speakerMutedRef.current;
        const confirmationSpeech = shouldSpeakSwitchConfirmation
          ? pendingVoiceSwitchAnnouncement?.text ?? ''
          : null;
        const shouldAttemptReadout =
          readUnheardMessagesOnSessionSwitch
          && !skipReadoutForAttachedMessage
          && readoutPlan.unreadMessages.length > 0
          && isActivatedRef.current
          && !speakerMutedRef.current;
        const speechQueue = [
          ...(confirmationSpeech ? [confirmationSpeech] : []),
          ...(shouldAttemptReadout ? readoutPlan.unreadSpeech : []),
        ];

        if (historyMessages.length > 0) {
          chat.loadHistory(historyMessages, gateway.id);
        }

        if (
          readoutPlan.unreadMessages.length === 0
          || !readUnheardMessagesOnSessionSwitch
          || skipReadoutForAttachedMessage
        ) {
          if (readoutPlan.nextCursor) {
            sessionReadCursorRef.current.set(destinationCursorKey, readoutPlan.nextCursor);
          } else {
            sessionReadCursorRef.current.delete(destinationCursorKey);
          }
        }

        if (speechQueue.length > 0) {
          pendingSessionReadoutRef.current = {
            cursorKey: destinationCursorKey,
            gatewayId: gateway.id,
            sessionKey,
            unreadMessages: shouldAttemptReadout ? readoutPlan.unreadMessages : [],
            speechQueue,
            trackedSpeechOffset: confirmationSpeech ? 1 : 0,
          };
        }

        remoteLog('voice', 'session_switch_readout', {
          gatewayId: gateway.id,
          sessionKey,
          sourceSessionKey,
          historyCount: historyMessages.length,
          unreadCount: readoutPlan.unreadMessages.length,
          spokenCount: speechQueue.length,
          switchConfirmationQueued: shouldSpeakSwitchConfirmation,
          voiceActive: isActivatedRef.current,
          speakerMuted: speakerMutedRef.current,
          skippedForAttachedMessage: skipReadoutForAttachedMessage,
          readoutDisabledInSettings: !readUnheardMessagesOnSessionSwitch,
          deferredUntilTurnEnd: speechQueue.length > 0 && isSessionReadoutPlaybackBlocked(),
        });

        if (speechQueue.length > 0) {
          maybeStartPendingSessionReadout();
        }

        historyLoadedRef.current.add(gateway.id);
      })
      .catch((err) => {
        if (
          pendingVoiceSwitchAnnouncement
          && switchOnlyVoiceCommandSessionKeyRef.current === sessionKey
        ) {
          queueStandaloneSessionAnnouncement(sessionKey, pendingVoiceSwitchAnnouncement.text);
          maybeStartPendingSessionReadout();
        }
        console.warn('[App] Failed to load session history:', err);
      })
      .finally(() => {
        const pendingLoad = pendingSessionSwitchLoadRef.current;
        if (
          pendingLoad?.loadId === loadId
          && pendingLoad.gatewayId === gateway.id
          && pendingLoad.sessionKey === sessionKey
        ) {
          pendingSessionSwitchLoadRef.current = null;
        }

        const pendingSkipReadout = skipNextSessionReadoutRef.current;
        if (
          pendingSkipReadout?.gatewayId === gateway.id
          && pendingSkipReadout.sessionKey === sessionKey
          && pendingSkipReadout.loadId === loadId
        ) {
          skipNextSessionReadoutRef.current = null;
        }
      });
  };

  // ── Keep session label map up to date from ticker items ──
  useEffect(() => {
    for (const item of agentTicker.items) {
      sessionLabelMapRef.current.set(item.sessionKey, item.label);
    }
  }, [agentTicker.items]);

  // ── Focus handler: switch to a subagent's session ──
  const handleTickerFocus = useCallback((item: { sessionKey: string }) => {
    sessionManager.switchSession(item.sessionKey);
  }, [sessionManager]);

  // ── Back to main handler ──
  const handleBackToMain = useCallback(() => {
    sessionManager.switchSession(mainSessionKeyRef.current);
  }, [sessionManager]);

  // ── Determine if we're focused on a non-main session ──
  const isNonMainSession = sessionManager.activeSessionKey !== mainSessionKeyRef.current;

  // ── Determine if we're focused on a sub-agent session (needs prominent back button) ──
  const isSubagentSession = sessionManager.activeSessionKey.includes(':subagent:');

  // ── Session Token Counter ──
  const [sessionTokens, setSessionTokens] = useState(0);

  // Reset token counter when active session changes
  const prevSessionKeyRef = useRef(sessionManager.activeSessionKey);
  useEffect(() => {
    if (sessionManager.activeSessionKey !== prevSessionKeyRef.current) {
      setSessionTokens(0);
      prevSessionKeyRef.current = sessionManager.activeSessionKey;
      // Update turn protocol session key
      turnProtocolRef.current?.setSessionKey(sessionManager.activeSessionKey);
    }
  }, [sessionManager.activeSessionKey]);

  // ── Text Input State ──
  const [textInput, setTextInput] = useState('');

  // ── Textarea Ref (for auto-grow) ──
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── File Upload State ──
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusText, setUploadStatusText] = useState('');
  const [uploadErrorText, setUploadErrorText] = useState('');
  const uploadErrorTimeoutRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showUploadError = useCallback((message: string) => {
    setUploadErrorText(message);
    if (uploadErrorTimeoutRef.current !== null) {
      window.clearTimeout(uploadErrorTimeoutRef.current);
    }
    uploadErrorTimeoutRef.current = window.setTimeout(() => {
      setUploadErrorText('');
      uploadErrorTimeoutRef.current = null;
    }, 6000);
  }, []);

  useEffect(() => () => {
    if (uploadErrorTimeoutRef.current !== null) {
      window.clearTimeout(uploadErrorTimeoutRef.current);
    }
  }, []);

  // ── File Upload Logic (Pages-backed + session attachment) ──
  const uploadAndSend = useCallback(async (files: File[]) => {
    remoteLog('upload', 'upload_attempt', { fileCount: files.length, hasWsClient: !!wsClient, isConnected, hasActiveGateway: !!activeGateway, activeGatewayId: activeGateway?.id });
    if (!files.length || !wsClient || !isConnected || !activeGateway) {
      remoteLog('upload', 'upload_blocked', { noFiles: !files.length, noWsClient: !wsClient, notConnected: !isConnected, noActiveGateway: !activeGateway });
      return;
    }

    setIsUploading(true);
    setUploadErrorText('');
    setUploadStatusText(files.length > 1 ? `Preparing ${files.length} uploads…` : 'Preparing upload…');

    try {
      const preparedUploads: Array<{
        meta: UploadedFileMeta;
        attachment: Awaited<ReturnType<typeof buildChatAttachment>>;
        attachedInline: boolean;
      }> = [];
      const uploadFailures: Array<{ filename: string; error: string }> = [];
      let inlineAttachmentBytesUsed = 0;

      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const displayName = file.name || 'upload';
        const batchLabel = files.length > 1 ? ` (${index + 1}/${files.length})` : '';
        setUploadStatusText(`Uploading ${displayName}${batchLabel}… 0%`);

        try {
          const meta = await uploadOneFile(file, {
            onProgress: (progress) => {
              setUploadStatusText(`Uploading ${progress.filename}${batchLabel}… ${progress.percent}%`);
            },
          });

          let attachment: Awaited<ReturnType<typeof buildChatAttachment>> = null;
          const effectiveInlineMaxBytes = getChatAttachmentInlineMaxBytes(meta.inlineAttachmentMaxBytes);
          if (file.size <= effectiveInlineMaxBytes
            && inlineAttachmentBytesUsed + file.size <= effectiveInlineMaxBytes) {
            attachment = await buildChatAttachment(file, {
              maxBytes: effectiveInlineMaxBytes - inlineAttachmentBytesUsed,
              pagesUpload: meta,
            });
            if (attachment) {
              inlineAttachmentBytesUsed += file.size;
            }
          }

          preparedUploads.push({
            meta: {
              ...meta,
              inlineAttachmentMaxBytes: effectiveInlineMaxBytes,
            },
            attachment,
            attachedInline: Boolean(attachment),
          });

          remoteLog('upload', 'upload_file_ready', {
            filename: meta.filename,
            path: meta.path,
            uploadId: meta.uploadId,
            attachedInline: Boolean(attachment),
            inlineAttachmentMaxBytes: meta.inlineAttachmentMaxBytes,
            inlineAttachmentBytesUsed,
            size: meta.size,
          });
        } catch (fileErr) {
          const fileMsg = getErrorMessage(fileErr);
          uploadFailures.push({ filename: displayName, error: fileMsg });
          remoteLog('upload', 'upload_file_error', {
            filename: displayName,
            index,
            error: fileMsg,
          });
        }
      }

      if (!preparedUploads.length) {
        const failureSummary = uploadFailures
          .map((failure) => `${failure.filename}: ${failure.error}`)
          .join('; ');
        throw new Error(failureSummary || 'No files were uploaded');
      }

      setUploadStatusText(preparedUploads.length > 1 ? `Sending ${preparedUploads.length} uploads…` : 'Sending upload…');
      const message = buildChatUploadBatchMessage(preparedUploads.map((item) => ({
        filename: item.meta.filename,
        path: item.meta.path,
        publicUrl: item.meta.publicUrl,
        attachedInline: item.attachedInline,
        inlineAttachmentMaxBytes: item.meta.inlineAttachmentMaxBytes,
      })));
      const attachments = preparedUploads.flatMap((item) => (item.attachment ? [item.attachment] : []));

      await sendUserChatMessage({
        sessionKey: sessionManager.activeSessionKey,
        displayMessage: message,
        gatewayId: activeGateway.id,
        gatewayName: activeGateway.name,
        source: 'upload',
        ...(attachments.length ? { attachments } : {}),
        extraLog: {
          fileCount: preparedUploads.length,
          failedCount: uploadFailures.length,
          inlineAttachmentCount: attachments.length,
          inlineAttachmentBytesUsed,
        },
      });

      remoteLog('upload', 'upload_complete_batch', {
        fileCount: preparedUploads.length,
        failedCount: uploadFailures.length,
        inlineAttachmentCount: attachments.length,
        inlineAttachmentBytesUsed,
      });

      if (uploadFailures.length) {
        const summary = uploadFailures
          .slice(0, 3)
          .map((failure) => `${failure.filename}: ${failure.error}`)
          .join('; ');
        showUploadError(`${uploadFailures.length} upload${uploadFailures.length === 1 ? '' : 's'} failed: ${summary}`);
      }
    } catch (err) {
      const msg = getErrorMessage(err);
      console.error('[App] File upload failed:', err);
      remoteLog('upload', 'upload_error', { error: msg });
      showUploadError(`Upload failed: ${msg}`);
    } finally {
      setIsUploading(false);
      setUploadStatusText('');
    }
  }, [wsClient, isConnected, activeGateway, sessionManager.activeSessionKey, showUploadError, sendUserChatMessage]);

  // ── Paste & Drag-and-Drop listeners ──
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) {
            // Give pasted images a descriptive name
            const ext = f.type.split('/')[1] || 'png';
            const named = new File([f], `screenshot-${Date.now()}.${ext}`, { type: f.type });
            files.push(named);
          }
        }
      }
      if (files.length) {
        e.preventDefault();
        void uploadAndSend(files);
      }
    };

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };

    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      const dropped = e.dataTransfer?.files;
      if (dropped && dropped.length) {
        void uploadAndSend(Array.from(dropped));
      }
    };

    document.addEventListener('paste', onPaste);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('paste', onPaste);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
    };
  }, [uploadAndSend]);

  // ── Native change listener for file input ──
  // Uses addEventListener directly on the DOM element instead of React's
  // onChange prop. This is more reliable across browsers, React versions,
  // and automated testing tools (Playwright/CDP) which may not trigger
  // React's synthetic event system when setting files programmatically.
  useEffect(() => {
    const input = fileInputRef.current;
    if (!input) return;
    const handleChange = () => {
      if (input.files && input.files.length > 0) {
        void uploadAndSend(Array.from(input.files));
      }
      input.value = '';
    };
    input.addEventListener('change', handleChange);
    return () => input.removeEventListener('change', handleChange);
  }, [uploadAndSend]);

  // ── Show gateway badge when >1 gateway configured ──
  const showGatewayBadge = gateways.length > 1;

  // ── Connection Status Text (for focused gateway) ──
  const connectionStatus = useMemo(() => {
    if (!activeGateway) return 'No Gateway';
    if (isConnected) return `Connected — ${activeGateway.name}`;
    if (isPairing) return 'Awaiting Approval';
    if (connectionState === 'connecting') return 'Connecting...';
    if (connectionState === 'origin_blocked') return 'Origin Blocked';
    if (connectionState === 'error') return 'Error';
    return 'Disconnected';
  }, [activeGateway, isConnected, isPairing, connectionState]);

  // ── Helper: extract complete sentences from buffer ──
  const doExtractSentences = useCallback((): string[] => {
    const extracted: string[] = [];
    let buf = sentenceBufferRef.current;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const match = buf.match(/^(.*?(?:[.!?]\s|\n))/s);
      if (!match) break;
      const sentence = match[1]!.trim();
      if (sentence) extracted.push(match[1]!);
      buf = buf.slice(match[1]!.length);
    }
    sentenceBufferRef.current = buf;
    return extracted;
  }, []);

  // ── Helper: wire up a StreamingHandler for a gateway ──
  const wireStreamingHandler = useCallback((gwId: string, client: WSClient) => {
    const handler = new StreamingHandler(client);

    /** Check if a session is subscribed (official Set OR fast-lane ref) */
    const isSub = (sk: string): boolean =>
      subscribedSessionsRef.current.has(sk) || immediateSubscribedRef.current.has(sk);

    // ── Thinking Events ──
    handler.onThinking((runId, text, sessionKey) => {
      // Determine session membership
      const thinkSession = sessionKey ?? runSessionMapRef.current.get(runId);
      const isActiveThinking = thinkSession === activeSessionKeyRef.current;
      const isSubscribedThinking = thinkSession ? isSub(thinkSession) : false;

      // Ignore thinking from sessions we're not watching
      if (!isActiveThinking && !isSubscribedThinking) return;

      // Register the run→session mapping early so onStreamStart/onDelta can find it
      if (sessionKey && !runSessionMapRef.current.has(runId)) {
        runSessionMapRef.current.set(runId, sessionKey);
      }


      // Strip "Reasoning:" prefix and Telegram italic markdown added by gateway's formatReasoningMessage()
      const cleanText = text
        .replace(/^Reasoning:\n?/, '')           // strip Reasoning: prefix
        .replace(/^_|_$/gm, '')                  // strip leading/trailing _ on each line (Telegram italic markdown)
        .trim();

      // Only show thinking for active or subscribed sessions
      const thinkTargetSession = sessionKey ?? runSessionMapRef.current.get(runId);
      const thinkIsActive = thinkTargetSession === activeSessionKeyRef.current;
      const thinkIsSub = thinkTargetSession ? isSub(thinkTargetSession) : false;
      if (!thinkIsActive && !thinkIsSub) return;

      let state = thinkingStateRef.current.get(runId);
      if (!state) {
        remoteLog('thinking', 'thinking_start', { runId, textLen: cleanText.length, sessionKey: thinkSession });
        state = { startTime: Date.now(), text: cleanText };
        thinkingStateRef.current.set(runId, state);

        // Create a thinking placeholder message in the chat UI
        const gw = gatewaysRef.current.find((g) => g.id === gwId);
        setMessagesRef.current((prev) => {
          if (prev.some((m) => m.runId === runId && m.role === 'assistant')) {
            return prev.map((m) =>
              m.runId === runId && m.role === 'assistant'
                ? { ...m, isThinking: true, thinkingText: cleanText }
                : m,
            );
          }
          // For subscribed sessions, include agentLabel so the bubble is properly labeled
          const agentLabelProps = isSubscribedThinking && thinkSession
            ? { agentLabel: `🔧 ${sessionLabelMapRef.current.get(thinkSession) ?? thinkSession.split(':').pop() ?? 'agent'}` }
            : {};
          return [...prev, {
            id: `thinking-${runId}-${Date.now()}`,
            role: 'assistant' as const,
            content: '',
            timestamp: new Date().toISOString(),
            isHistory: false,
            isStreaming: true,
            ttsStatus: 'none' as const,
            runId,
            gatewayId: gwId,
            gatewayName: gw?.name,
            isThinking: true,
            thinkingText: cleanText,
            ...agentLabelProps,
          }];
        });
      } else {
        // text is the full accumulated reasoning, not a delta — assign, don't append
        state.text = cleanText;
        setMessagesRef.current((prev) =>
          prev.map((m) =>
            m.runId === runId && m.role === 'assistant' && m.isThinking
              ? { ...m, thinkingText: state!.text }
              : m,
          ),
        );
      }
    });

    // ── Tool Call Display ──
    handler.onToolCall((runId, toolName, input, sessionKey) => {
      const targetSession = sessionKey ?? runSessionMapRef.current.get(runId);
      const isActive = targetSession === activeSessionKeyRef.current;
      const isToolSubscribed = targetSession ? isSub(targetSession) : false;
      if (!isActive && !isToolSubscribed) return;

      const arg = extractToolArg(toolName, input);

      // Tool events can arrive before the placeholder is patched to the real
      // runId. Claim the active-session placeholder early so the tool call is
      // not dropped while waiting for the first delta / chat.send response.
      if (isActive) {
        patchPlaceholderRunIdRef.current(runId);
      }

      setMessagesRef.current((prev) => appendToolCallToMessages(
        prev,
        runId,
        { name: toolName, arg },
        () => {
          const gw = gatewaysRef.current.find((g) => g.id === gwId);
          const label = targetSession
            ? sessionLabelMapRef.current.get(targetSession) ?? targetSession.split(':').pop() ?? 'agent'
            : 'agent';

          return {
            id: `${isToolSubscribed ? 'assistant-sub' : 'assistant'}-${runId}-${Date.now()}`,
            role: 'assistant' as const,
            content: '',
            timestamp: new Date().toISOString(),
            isHistory: false,
            isStreaming: true,
            ttsStatus: 'none' as const,
            runId,
            gatewayId: gwId,
            gatewayName: gw?.name,
            ...(isToolSubscribed ? { agentLabel: `🔧 ${label}` } : {}),
          };
        },
      ));

      // Ensure runSessionMap is populated for future lookups (e.g., onDelta, onComplete).
      // onStreamStart normally sets this, but if onToolCall runs first, it's not set yet.
      if (targetSession && !runSessionMapRef.current.has(runId)) {
        runSessionMapRef.current.set(runId, targetSession);
      }

      runIdsWithActiveToolRef.current.add(runId);
    });

    handler.onToolResult((runId, toolName, sessionKey) => {
      const targetSession = sessionKey ?? runSessionMapRef.current.get(runId);
      const isActive = targetSession === activeSessionKeyRef.current;
      const isToolSubscribed = targetSession ? isSub(targetSession) : false;
      if (!isActive && !isToolSubscribed) return;

      setMessagesRef.current((prev) => markLatestToolCallDone(prev, runId, toolName));
    });

    // ── Tool Progress Updates ──
    handler.onToolUpdate((runId, toolName, data, sessionKey) => {
      const targetSession = sessionKey ?? runSessionMapRef.current.get(runId);
      const isActive = targetSession === activeSessionKeyRef.current;
      const isToolSubscribed = targetSession ? isSub(targetSession) : false;
      if (!isActive && !isToolSubscribed) return;

      // Extract a progress description from the update data
      const progressArg = typeof data?.partialResult === 'string'
        ? data.partialResult.substring(0, 200)
        : typeof data?.progress === 'string'
          ? data.progress
          : typeof data?.status === 'string'
            ? data.status
            : extractToolArg(toolName, data) ?? undefined;
      if (progressArg) {
        setMessagesRef.current((prev) => updateToolCallProgress(prev, runId, toolName, progressArg));
      }
    });

    // ── Compaction Events ──
    handler.onCompaction((_runId, phase, sessionKey) => {
      if (phase !== 'end') return;
      const isActive = sessionKey === activeSessionKeyRef.current;
      if (!isActive) return;

      // Insert compaction divider in the UI
      setMessagesRef.current((prev) => [...prev, {
        id: `compaction-${Date.now()}`,
        role: 'system' as const,
        content: '🧹 Context compacted',
        timestamp: new Date().toISOString(),
        isHistory: false,
        isStreaming: false,
        ttsStatus: 'none' as const,
        isCompaction: true,
      }]);

      // Refresh chat history to get the compacted context
      const client = getClient(gwId);
      if (client && sessionKey) {
        const gw = gatewaysRef.current.find((g) => g.id === gwId);
        fetchChatHistoryAsMessages(client, sessionKey, 30, gwId, gw?.name)
          .then((historyMessages) => {
            if (historyMessages.length > 0) {
              // Replace history messages but keep live messages
              setMessagesRef.current((prev) => {
                const liveMessages = prev.filter((m) => !m.isHistory);
                return [...historyMessages, ...liveMessages];
              });
            }
          })
          .catch((err) => console.warn('[App] Failed to refresh history after compaction:', err));
      }
    });

    // ── Lifecycle Events ──
    handler.onLifecycle((runId, phase, sessionKey) => {
      if (phase === 'error') {
        const targetSession = sessionKey ?? runSessionMapRef.current.get(runId);
        const isActive = targetSession === activeSessionKeyRef.current;
        if (isActive) {
          console.warn('[App] Agent lifecycle error for run:', runId);
        }
      }
      // start/end are primarily handled via the chat event path
      // (chat final/error events). Lifecycle events provide redundancy.
    });

    handler.onStreamStart((runId, sessionKey) => {
      runSessionMapRef.current.set(runId, sessionKey);
      const isActiveSession = sessionKey === activeSessionKeyRef.current;
      let isSubscribedSession = isSub(sessionKey);

      // Auto-subscribe subagent sessions based on depth setting.
      // This bridges the race between stream start and the ticker poll.
      if (!isSubscribedSession && !isActiveSession && sessionKey.includes('subagent')) {
        const depthSetting = parseInt(localStorage.getItem(STORAGE_KEYS.SUBAGENT_DEPTH) ?? '0', 10);
        // Count occurrences of ':subagent:' to determine session depth
        const sessionDepth = (sessionKey.match(/:subagent:/g) || []).length;
        if (depthSetting > 0 && sessionDepth <= depthSetting) {
          immediateSubscribedRef.current.add(sessionKey);
          agentTickerSubscribeRef.current(sessionKey);
          isSubscribedSession = true;
        }
      }

      remoteLog('ws', 'chat_stream_start', {
        runId,
        sessionKey,
        route: isActiveSession ? 'active' : isSubscribedSession ? 'subscribed' : 'ignored',
      });

      if (isActiveSession) {
        currentRunIdRef.current = runId;
        sentenceBufferRef.current = '';
        deltaAccumulatedRef.current = '';
        ttsSuppressionRef.current = null; // New response — clear TTS suppression
        voiceRef.current.resetVoiceLock(runId);
        // Reset TTS debug order counter for new stream
        ttsDebugOrderRef.current = 0;
        // Initialize delta time tracker for active session gap detection
        lastDeltaTimeRef.current.set(runId, Date.now());
      }

      // Transition thinking indicator: if we were thinking, calculate duration
      // and collapse the indicator now that streaming is starting.
      const thinkingState = thinkingStateRef.current.get(runId);
      if (thinkingState) {
        const durationSec = Math.round((Date.now() - thinkingState.startTime) / 1000);
        setMessagesRef.current((prev) =>
          prev.map((m) =>
            m.runId === runId && m.role === 'assistant'
              ? { ...m, isThinking: false, thinkingDuration: durationSec, toolCalls: m.toolCalls }
              : m,
          ),
        );
        thinkingStateRef.current.delete(runId);
      }

      // Try to patch the oldest waiting placeholder's temporary local-* runId
      // to the real gateway runId. This is the critical step that ensures
      // subsequent deltas/final match the correct message bubble by exact runId.
      remoteLog('placeholder', 'dbg_onStreamStart', { runId, sessionKey, isActiveSession });
      // Log pending local-* placeholders before patching
      setMessagesRef.current((prev) => {
        const localPlaceholders = prev.filter(
          (m) => m.role === 'assistant' && m.isStreaming && m.runId?.startsWith('local-'),
        ).length;
        remoteLog('placeholder', 'dbg_pending_count', { localPlaceholders, runId });
        return prev; // read-only — no mutation
      });
      // Only try to patch placeholders for the active session — placeholders
      // are created by user messages which always target the active session.
      // Subscribed sub-agent streams must never claim user-message placeholders.
      const patched = isActiveSession ? patchPlaceholderRunIdRef.current(runId) : false;
      remoteLog('placeholder', 'dbg_onStreamStart_patch_result', { runId, patched, isActiveSession });

      if (!patched) {
        if (isSubscribedSession) {
          // Subscribed session (subagent activity) — create message with agent label
          const gw = gatewaysRef.current.find((g) => g.id === gwId);
          const label = sessionLabelMapRef.current.get(sessionKey) ?? sessionKey.split(':').pop() ?? 'agent';
          setMessagesRef.current((prev) => {
            // Race guard: skip if a message with this runId already exists
            if (prev.some((m) => m.runId === runId && m.role === 'assistant')) {
              return prev;
            }
            return [...prev, {
              id: `assistant-sub-${runId}-${Date.now()}`,
              role: 'assistant' as const,
              content: '',
              timestamp: new Date().toISOString(),
              isHistory: false,
              isStreaming: true,
              ttsStatus: 'none' as const,
              runId,
              gatewayId: gwId,
              gatewayName: gw?.name,
              agentLabel: `🔧 ${label}`,
            }];
          });
          // Initialize delta time tracker for split mode
          lastSubagentDeltaTimeRef.current.set(runId, Date.now());
        } else if (isActiveSession) {
          // No placeholder found — server-initiated message (proactive, etc.)
          setMessagesRef.current((prev) => {
            // Double-check no message with this runId exists (race guard)
            if (prev.some((m) => m.runId === runId && m.role === 'assistant')) {
              return prev;
            }
            const gw = gatewaysRef.current.find((g) => g.id === gwId);
            return [...prev, {
              id: `assistant-server-${Date.now()}`,
              role: 'assistant' as const,
              content: '',
              timestamp: new Date().toISOString(),
              isHistory: false,
              isStreaming: true,
              ttsStatus: 'none' as const,
              runId,
              gatewayId: gwId,
              gatewayName: gw?.name,
            }];
          });
        }
      }
    });

    handler.onDelta((runId, partialText, _fullText, seq) => {
      const runSession = runSessionMapRef.current.get(runId);
      const isActiveSession = runSession === activeSessionKeyRef.current;
      const isSubscribedSession = runSession ? isSub(runSession) : false;

      // Update the message in chat for active session OR subscribed sessions
      if (isActiveSession || isSubscribedSession) {
        const bubbleMode = isSubscribedSession
          ? (localStorage.getItem(STORAGE_KEYS.SUBAGENT_BUBBLE_MODE) ?? 'follow')
          : null;

        if (isSubscribedSession && bubbleMode === 'follow') {
          // FOLLOW MODE: move message to bottom of array on each delta
          // Gap detection: insert line break when resuming after tool call pause
          const FOLLOW_GAP_MS = 2000;
          const followNow = Date.now();
          const followLastTime = lastSubagentDeltaTimeRef.current.get(runId) ?? 0;
          const followGap = followNow - followLastTime;
          lastSubagentDeltaTimeRef.current.set(runId, followNow);

          setMessagesRef.current((prev) => {
            const idx = prev.findIndex((m) => m.runId === runId && m.role === 'assistant');
            if (idx < 0) return prev;
            const msg = prev[idx];
            // Backfill agentLabel if missing (happens when subscribed mid-stream)
            const labelPatch = !msg.agentLabel && runSession ? (() => {
              const lbl = sessionLabelMapRef.current.get(runSession) ?? runSession.split(':').pop() ?? 'agent';
              return { agentLabel: `🔧 ${lbl}` };
            })() : {};
            const followSeparator = (followGap >= FOLLOW_GAP_MS && followLastTime > 0 && msg.content.length > 0) ? '\n\n' : '';
            const updatedMsg = { ...msg, content: msg.content + followSeparator + partialText, ...labelPatch };
            // If already at the end, just update in-place
            if (idx === prev.length - 1) {
              const updated = [...prev];
              updated[idx] = updatedMsg;
              return updated;
            }
            // Move to bottom
            const without = prev.filter((_, i) => i !== idx);
            return [...without, updatedMsg];
          });
        } else if (isSubscribedSession && bubbleMode === 'split') {
          // SPLIT MODE: close old bubble and create new one after 2s gap
          const SPLIT_GAP_MS = 2000;
          const now = Date.now();
          const lastTime = lastSubagentDeltaTimeRef.current.get(runId) ?? 0;
          const gap = now - lastTime;
          lastSubagentDeltaTimeRef.current.set(runId, now);

          if (gap >= SPLIT_GAP_MS && lastTime > 0) {
            // Gap detected — split only if the bubble got buried by other messages
            setMessagesRef.current((prev) => {
              const idx = prev.findIndex(
                (m) => m.runId === runId && m.role === 'assistant' && m.isStreaming,
              );
              if (idx < 0) return prev;
              const oldMsg = prev[idx];
              // Backfill agentLabel if missing (subscribed mid-stream)
              const resolvedLabel = oldMsg.agentLabel ?? (runSession ? (() => {
                const lbl = sessionLabelMapRef.current.get(runSession) ?? runSession.split(':').pop() ?? 'agent';
                return `🔧 ${lbl}`;
              })() : undefined);

              // If the streaming bubble is still the last message, no need to split —
              // just append in-place (same as the "no gap" path).
              if (idx === prev.length - 1) {
                const updated = [...prev];
                updated[idx] = { ...oldMsg, content: oldMsg.content + partialText };
                return updated;
              }

              // Other messages appeared after this bubble — split it.
              const updated = [...prev];
              // Finalize old bubble (keep its accumulated content)
              updated[idx] = { ...oldMsg, isStreaming: false, ...(resolvedLabel ? { agentLabel: resolvedLabel } : {}) };
              // Create new bubble at end with same runId
              updated.push({
                id: `assistant-sub-${runId}-split-${Date.now()}`,
                role: 'assistant' as const,
                content: partialText,
                timestamp: new Date().toISOString(),
                isHistory: false,
                isStreaming: true,
                ttsStatus: 'none' as const,
                runId,
                gatewayId: gwId,
                gatewayName: oldMsg.gatewayName,
                agentLabel: resolvedLabel,
              });
              return updated;
            });
          } else {
            // No gap — update streaming bubble in-place
            setMessagesRef.current((prev) => {
              const idx = prev.findIndex(
                (m) => m.runId === runId && m.role === 'assistant' && m.isStreaming,
              );
              if (idx >= 0) {
                const updated = [...prev];
                // Backfill agentLabel if missing (subscribed mid-stream)
                const labelPatch = !updated[idx].agentLabel && runSession ? (() => {
                  const lbl = sessionLabelMapRef.current.get(runSession) ?? runSession.split(':').pop() ?? 'agent';
                  return { agentLabel: `🔧 ${lbl}` };
                })() : {};
                updated[idx] = { ...updated[idx], content: updated[idx].content + partialText, ...labelPatch };
                return updated;
              }
              return prev;
            });
          }
        } else {
          // Active session: keep main agent reply at the bottom so it's always
          // visible even when sub-agent bubbles were inserted above it.
          // Gap detection: insert line break when resuming after tool call pause
          const ACTIVE_GAP_MS = 2000;
          const activeNow = Date.now();
          const activeLastTime = lastDeltaTimeRef.current.get(runId) ?? 0;
          const activeGapMs = activeNow - activeLastTime;
          lastDeltaTimeRef.current.set(runId, activeNow);

          setMessagesRef.current((prev) => {
            const idx = prev.findIndex(
              (m) => m.runId === runId && m.role === 'assistant',
            );
            if (idx < 0) {
              // Fallback for active session: try isStreaming match
              if (isActiveSession) {
                const fallbackIdx = prev.findIndex(
                  (m) => m.isStreaming && m.role === 'assistant',
                );
                if (fallbackIdx >= 0) {
                  const updated = [...prev];
                  const fallbackSeparator = (activeGapMs >= ACTIVE_GAP_MS && activeLastTime > 0 && updated[fallbackIdx].content.length > 0) ? '\n\n' : '';
                  updated[fallbackIdx] = {
                    ...updated[fallbackIdx],
                    content: updated[fallbackIdx].content + fallbackSeparator + partialText,
                    runId,
                  };
                  return updated;
                }
              }
              return prev;
            }
            const msg = prev[idx];
            const activeSeparator = (activeGapMs >= ACTIVE_GAP_MS && activeLastTime > 0 && msg.content.length > 0) ? '\n\n' : '';
            const updatedMsg = { ...msg, content: msg.content + activeSeparator + partialText };
            // If already at the end, just update in-place
            if (idx === prev.length - 1) {
              const updated = [...prev];
              updated[idx] = updatedMsg;
              return updated;
            }
            // Move to bottom
            const without = prev.filter((_, i) => i !== idx);
            return [...without, updatedMsg];
          });
        }
      }

      // When deltas arrive, any running tools for this runId are complete
      markToolsDone(runId);

      if (isActiveSession) {
        if (seq === 0) {
          sendRef.current({ type: 'SSE_FIRST_TOKEN' });
          playAudioCue('firstToken', speakerMutedRef.current, isActivatedRef.current);
        }

        // TTS: buffer text and extract sentences for the focused gateway/session.
        // Don't gate on isSpeaking — voice interrupts are handled by ttsSuppressionRef.
        if (gwId === activeGatewayIdRef.current && !speakerMutedRef.current && isActivatedRef.current && ttsSuppressionRef.current !== runId) {
          sentenceBufferRef.current += partialText;
          deltaAccumulatedRef.current += partialText;

          const sentences = doExtractSentences();
          for (const sentence of sentences) {
            remoteLog('tts', 'delta_sentence', { text: sentence.trim() });
            voiceRef.current.speak(sentence);
            // Track for TTS debug overlay (per-message)
            const order = ++ttsDebugOrderRef.current;
            appendTtsDebugSentence(runId, { text: sentence.trim(), order, timestamp: Date.now() });
          }

          // Flush timer: if no new deltas arrive for 3 seconds, speak whatever
          // remains in the sentence buffer (e.g. trailing text without punctuation).
          // This is safe because speak() is now serialized via the chain.
          if (flushTimerRef.current) {
            clearTimeout(flushTimerRef.current);
          }
          flushTimerRef.current = setTimeout(() => {
            const remaining = sentenceBufferRef.current.trim();
            if (remaining && !speakerMutedRef.current && isActivatedRef.current && ttsSuppressionRef.current !== runId) {
              remoteLog('tts', 'flush_timer_speak', { text: remaining });
              sentenceBufferRef.current = '';
              voiceRef.current.speak(remaining);
              // Track for TTS debug overlay (per-message)
              const flushOrder = ++ttsDebugOrderRef.current;
              appendTtsDebugSentence(runId, { text: remaining, order: flushOrder, timestamp: Date.now() });
            }
          }, 3000);
        }
      }
      // NOTE: subscribed session deltas do NOT trigger TTS — text only
    });

    handler.onComplete((runId, content, usage, _stopReason, sessionKey) => {
      markToolsDone(runId);
      let runSession = runSessionMapRef.current.get(runId);

      // ── Deferred placeholder patch ──
      // When a fast response completes before onStreamStart fires,
      // runSessionMapRef won't have this runId yet. If there's an unpatched
      // local-* placeholder waiting, claim it now so the response fills the
      // existing bubble instead of creating a duplicate via the inject path.
      if (!runSession && sessionKey === activeSessionKeyRef.current) {
        let claimed = false;
        setMessagesRef.current((prev) => {
          // Already exists (eager patch or onStreamStart won the race)
          if (prev.some((m) => m.runId === runId && m.role === 'assistant')) {
            claimed = true;
            return prev;
          }
          // Find the oldest unmatched placeholder
          const idx = prev.findIndex(
            (m) => m.role === 'assistant' && m.isStreaming && m.runId?.startsWith('local-') && !m.content,
          );
          if (idx < 0) return prev;
          claimed = true;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], runId };
          return updated;
        });
        if (claimed) {
          runSessionMapRef.current.set(runId, sessionKey);
          runSession = sessionKey;
          remoteLog('placeholder', 'dbg_deferred_patch_onComplete', { runId, sessionKey });
        }
      }

      const wsSummary = buildWsMessageSummary(content);
      const isActiveSession = runSession === activeSessionKeyRef.current;
      const isSubscribedSession = runSession ? isSub(runSession) : false;
      remoteLog('ws', 'chat_final', {
        runId,
        sessionKey,
        trackedSession: runSession ?? null,
        route: isActiveSession ? 'active' : isSubscribedSession ? 'subscribed' : 'untracked',
        ...wsSummary,
      });
      runSessionMapRef.current.delete(runId);

      // Finalize any remaining thinking state (edge case: thinking → final with no deltas)
      const thinkingState = thinkingStateRef.current.get(runId);
      if (thinkingState) {
        const durationSec = Math.round((Date.now() - thinkingState.startTime) / 1000);
        setMessagesRef.current((prev) =>
          prev.map((m) =>
            m.runId === runId && m.role === 'assistant'
              ? { ...m, isThinking: false, thinkingDuration: durationSec, toolCalls: m.toolCalls }
              : m,
          ),
        );
        thinkingStateRef.current.delete(runId);
      }

      // Handle runs that complete without ever creating a visible assistant
      // bubble (for example announce-triggered/server-initiated replies with no
      // user placeholder and no preceding delta). If the run belongs to the
      // active or subscribed session but no assistant message exists yet,
      // inject the final-only bubble here instead of silently finalizing
      // against a missing runId.
      const resolvedSessionKey = runSession ?? sessionKey;
      const hasAssistantBubble = messagesRef.current.some(
        (m) => m.runId === runId && m.role === 'assistant',
      );
      // Handle proactive messages (heartbeat/cron) that arrive with no session key.
      // Treat them as active-session messages so they appear in the chat and get TTS.
      const isProactiveMessage = !resolvedSessionKey && !hasAssistantBubble && runId.startsWith('webchat-proactive-');
      if (isProactiveMessage && content?.trim()) {
        const finalOnlyKind = getAssistantMessageKind(content);
        const shouldRender = shouldRenderFinalOnlyAssistantMessage(finalOnlyKind);
        remoteLog('ws', 'chat_proactive', {
          runId,
          render: shouldRender ? 'inject' : 'drop',
          contentKind: finalOnlyKind,
          ...buildWsMessageSummary(content),
        });
        if (shouldRender) {
          const gw = gatewaysRef.current.find((g) => g.id === gwId);
          setMessagesRef.current((prev) => injectFinalOnlyAssistantMessage(prev, {
            route: 'active',
            runId,
            content,
            gatewayId: gwId,
            gatewayName: gw?.name,
            inputTokens: usage?.inputTokens,
            outputTokens: usage?.outputTokens,
          }));
          // TTS for proactive messages
          if (gwId === activeGatewayIdRef.current && !speakerMutedRef.current && isActivatedRef.current) {
            voiceRef.current.speak(content);
          }
        }
        return;
      }
      if (!hasAssistantBubble && resolvedSessionKey) {
        const finalOnlyKind = getAssistantMessageKind(content);
        const isActiveBackground = resolvedSessionKey === activeSessionKeyRef.current;
        const isSubscribedBackground = isSub(resolvedSessionKey);

        if (isActiveBackground) {
          const shouldRender = shouldRenderFinalOnlyAssistantMessage(finalOnlyKind);
          remoteLog('ws', 'chat_final_only', {
            runId,
            sessionKey: resolvedSessionKey,
            route: 'active',
            render: shouldRender ? 'inject' : 'drop',
            contentKind: finalOnlyKind,
            ...wsSummary,
          });

          if (!shouldRender) {
            return;
          }

          const gw = gatewaysRef.current.find((g) => g.id === gwId);
          setMessagesRef.current((prev) => injectFinalOnlyAssistantMessage(prev, {
            route: 'active',
            runId,
            content,
            gatewayId: gwId,
            gatewayName: gw?.name,
            inputTokens: usage?.inputTokens,
            outputTokens: usage?.outputTokens,
          }));

          Promise.resolve(sessionManagerRef.current.refreshAll()).then(() => {
            const findSession = sessionManagerRef.current.findSession;
            if (typeof findSession !== 'function') {
              return;
            }
            const total = findSession(activeSessionKeyRef.current)?.totalTokens;
            if (total && total > 0) setSessionTokens(total);
          });
          return;
        }

        if (isSubscribedBackground) {
          const shouldRender = shouldRenderFinalOnlyAssistantMessage(finalOnlyKind);
          remoteLog('ws', 'chat_final_only', {
            runId,
            sessionKey: resolvedSessionKey,
            route: 'subscribed',
            render: shouldRender ? 'inject' : 'drop',
            contentKind: finalOnlyKind,
            ...wsSummary,
          });

          if (shouldRender) {
            const gw = gatewaysRef.current.find((g) => g.id === gwId);
            const label = sessionLabelMapRef.current.get(resolvedSessionKey)
              ?? resolvedSessionKey.split(':').pop() ?? 'agent';
            setMessagesRef.current((prev) => injectFinalOnlyAssistantMessage(prev, {
              route: 'subscribed',
              runId,
              content,
              gatewayId: gwId,
              gatewayName: gw?.name,
              agentLabel: `🔧 ${label}`,
              inputTokens: usage?.inputTokens,
              outputTokens: usage?.outputTokens,
            }));
          }
          return;
        }
      }

      if (isActiveSession) {
        // Clear flush timer — onComplete does its own flush
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }

        // Append any text from the final content that wasn't delivered via deltas.
        // The gateway's final event contains the complete response, but the last
        // few tokens may not arrive as individual delta events.
        if (content.length > deltaAccumulatedRef.current.length) {
          const missingText = content.substring(deltaAccumulatedRef.current.length);
          sentenceBufferRef.current += missingText;
          remoteLog('tts', 'onComplete_missing_appended', { missingText, deltaLen: deltaAccumulatedRef.current.length, contentLen: content.length });
        }

        // Always clear the sentence buffer on stream complete to prevent stale text
        // leaking into the next response. Speak remaining text if conditions allow.
        const remaining = sentenceBufferRef.current.trim();
        sentenceBufferRef.current = '';
        deltaAccumulatedRef.current = '';
        const hasToolCalls = (() => {
          let found = false;
          setMessagesRef.current((prev) => {
            const msg = prev.find((m) => m.runId === runId && m.role === 'assistant');
            if (msg?.toolCalls && msg.toolCalls.length > 0) found = true;
            return prev; // read-only
          });
          return found;
        })();
        const finalKind = getAssistantMessageKind(content, { hasToolCalls });
        const isNoReplyMsg = finalKind === 'no_reply' || finalKind === 'heartbeat' || finalKind === 'blank';
        remoteLog('tts', 'onComplete_flush', { text: remaining });
        remoteLog('ws', 'chat_final_render', {
          runId,
          sessionKey,
          route: 'active',
          render:
            finalKind === 'heartbeat'
              ? 'cleanup_heartbeat'
              : finalKind === 'no_reply' || finalKind === 'blank'
                ? 'mark_no_reply'
                : 'finalize',
          contentKind: finalKind,
          hasToolCalls,
          ...wsSummary,
        });

        const ttsConditions = {
          hasRemaining: !!remaining,
          remainingPreview: remaining ? remaining : '(empty)',
          gwMatch: gwId === activeGatewayIdRef.current,
          speakerNotMuted: !speakerMutedRef.current,
          isActivated: isActivatedRef.current,
          notSuppressed: ttsSuppressionRef.current !== runId,
          suppressionValue: ttsSuppressionRef.current,
          runId,
        };
        remoteLog('tts', 'onComplete', ttsConditions);

        // Direct fire-and-forget log to backend (bypass buffered logger as fallback)
        try {
          const _authHeaders = getApiAuthHeaders('voice-ai-edge-tts-token');
          if (Object.keys(_authHeaders).length > 0) {
            fetch(`${getApiUrl()}/api/log`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ..._authHeaders },
              body: JSON.stringify({
                events: [{ ts: new Date().toISOString(), cat: 'tts', evt: 'onComplete_direct', data: ttsConditions }],
                ctx: { source: 'beta-native-direct' },
              }),
              keepalive: true,
              credentials: 'omit',
            }).catch(() => {});
          }
        } catch { /* ignore */ }

        if (remaining && gwId === activeGatewayIdRef.current && !speakerMutedRef.current && isActivatedRef.current && ttsSuppressionRef.current !== runId && !isNoReplyMsg) {
          remoteLog('tts', 'flush_speak', { text: remaining });
          voiceRef.current.speak(remaining);
          // Track for TTS debug overlay (per-message)
          const order = ++ttsDebugOrderRef.current;
          appendTtsDebugSentence(runId, { text: remaining, order, timestamp: Date.now() });
        } else if (remaining) {
          remoteLog('tts', 'flush_DROPPED', { text: remaining, conditions: ttsConditions });
        }

        // Log toolCalls state before finalization for debugging
        setMessagesRef.current((prev) => {
          const msg = prev.find((m) => m.runId === runId && m.role === 'assistant');
          if (msg) {
            remoteLog('toolcalls', 'pre_finalize', {
              runId,
              toolCallCount: msg.toolCalls?.length ?? 0,
              toolCalls: msg.toolCalls?.map((t) => `${t.status}:${t.name}`),
              hasContent: !!msg.content,
              isStreaming: msg.isStreaming,
            });
          }
          return prev; // read-only
        });

        finalizeStreamingRef.current(runId, content, usage);

        // Refresh session data to get updated totalTokens from the gateway
        if (sessionKey === activeSessionKeyRef.current) {
          Promise.resolve(sessionManagerRef.current.refreshAll()).then(() => {
            const findSession = sessionManagerRef.current.findSession;
            if (typeof findSession !== 'function') {
              return;
            }
            const total = findSession(activeSessionKeyRef.current)?.totalTokens;
            if (total && total > 0) setSessionTokens(total);
          });
        }

        // Log toolCalls state after finalization for debugging
        setMessagesRef.current((prev) => {
          const msg = prev.find((m) => m.runId === runId && m.role === 'assistant');
          if (msg) {
            remoteLog('toolcalls', 'post_finalize', {
              runId,
              toolCallCount: msg.toolCalls?.length ?? 0,
              toolCalls: msg.toolCalls?.map((t) => `${t.status}:${t.name}`),
              isStreaming: msg.isStreaming,
            });
          }
          // Check for duplicate messages with same runId
          const dupes = prev.filter((m) => m.runId === runId && m.role === 'assistant');
          if (dupes.length > 1) {
            remoteLog('toolcalls', 'DUPLICATE_DETECTED', {
              runId,
              count: dupes.length,
              ids: dupes.map((d) => d.id),
              toolCallCounts: dupes.map((d) => d.toolCalls?.length ?? 0),
            });
          }
          return prev; // read-only
        });

        // Handle protocol-only messages
        if (finalKind === 'heartbeat') {
          // Heartbeats are invisible — remove both assistant and preceding user message
          remoteLog('placeholder', 'dbg_cleanup_heartbeat', { runId, content: content.substring(0, 20) });
          setMessagesRef.current((prev) => {
            const assistantIdx = prev.findIndex(
              (m) => m.runId === runId && m.role === 'assistant',
            );
            if (assistantIdx < 0) return prev;
            const userIdx =
              assistantIdx > 0 && prev[assistantIdx - 1].role === 'user'
                ? assistantIdx - 1
                : -1;
            return prev.filter((_, i) => i !== assistantIdx && i !== userIdx);
          });
        } else if (finalKind === 'no_reply' || finalKind === 'blank') {
          // NO_REPLY / blank acknowledgements — render the assistant message subtly
          remoteLog('placeholder', 'dbg_mark_no_reply', {
            runId,
            content: content.substring(0, 20),
            gatewaySuppress: finalKind === 'blank',
          });
          setMessagesRef.current((prev) =>
            prev.map((m) =>
              m.runId === runId && m.role === 'assistant'
                ? { ...m, isNoReply: true }
                : m,
            ),
          );
          playAudioCue('noReply', speakerMutedRef.current, isActivatedRef.current);
        }

        currentRunIdRef.current = null;
        lastDeltaTimeRef.current.delete(runId);
        sendRef.current({ type: 'SSE_DONE' });
      } else if (isSubscribedSession) {
        const bubbleMode = localStorage.getItem(STORAGE_KEYS.SUBAGENT_BUBBLE_MODE) ?? 'follow';
        // Compute agentLabel backfill for messages that were created before subscription
        const labelBackfill = runSession ? (() => {
          const lbl = sessionLabelMapRef.current.get(runSession) ?? runSession.split(':').pop() ?? 'agent';
          return `🔧 ${lbl}`;
        })() : undefined;
        if (bubbleMode === 'split') {
          // Split mode: only finalize the currently-streaming bubble, keep its accumulated content
          setMessagesRef.current((prev) =>
            prev.map((m) =>
              m.runId === runId && m.role === 'assistant' && m.isStreaming
                ? { ...m, isStreaming: false, toolCalls: m.toolCalls, ...(!m.agentLabel && labelBackfill ? { agentLabel: labelBackfill } : {}) }
                : m,
            ),
          );
        } else {
          // Follow mode / default: set full final content on the message
          setMessagesRef.current((prev) =>
            prev.map((m) =>
              m.runId === runId && m.role === 'assistant'
                ? { ...m, content, isStreaming: false, toolCalls: m.toolCalls, ...(!m.agentLabel && labelBackfill ? { agentLabel: labelBackfill } : {}) }
                : m,
            ),
          );
        }
        // Clean up split mode tracking
        lastSubagentDeltaTimeRef.current.delete(runId);
      }
    });

    handler.onAbort((runId) => {
      markToolsDone(runId);
      const runSession = runSessionMapRef.current.get(runId);
      const isActiveSession = runSession === activeSessionKeyRef.current;
      const isSubscribedSession = runSession ? isSub(runSession) : false;
      runSessionMapRef.current.delete(runId);
      thinkingStateRef.current.delete(runId);

      if (isActiveSession) {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        sentenceBufferRef.current = '';
        abortStreamingRef.current(runId);
        currentRunIdRef.current = null;
        lastDeltaTimeRef.current.delete(runId);
        sendRef.current({ type: 'CHAT_ABORTED' });
      } else if (isSubscribedSession) {
        // Target only the streaming bubble (split mode may have finalized earlier ones)
        setMessagesRef.current((prev) =>
          prev.map((m) =>
            m.runId === runId && m.role === 'assistant' && m.isStreaming
              ? { ...m, isStreaming: false, content: m.content || '[Aborted]', toolCalls: m.toolCalls }
              : m,
          ),
        );
        lastSubagentDeltaTimeRef.current.delete(runId);
      }
    });

    handler.onError((runId, errorMessage) => {
      const normalizedErrorMessage = getErrorMessage(errorMessage);
      markToolsDone(runId);
      const runSession = runSessionMapRef.current.get(runId);
      const isActiveSession = runSession === activeSessionKeyRef.current;
      const isSubscribedSession = runSession ? isSub(runSession) : false;
      runSessionMapRef.current.delete(runId);
      thinkingStateRef.current.delete(runId);

      if (isActiveSession) {
        if (flushTimerRef.current) {
          clearTimeout(flushTimerRef.current);
          flushTimerRef.current = null;
        }
        sentenceBufferRef.current = '';
        errorStreamingRef.current(runId, normalizedErrorMessage);
        currentRunIdRef.current = null;
        lastDeltaTimeRef.current.delete(runId);
        sendRef.current({ type: 'SSE_ERROR' });
        playAudioCue('error', speakerMutedRef.current, isActivatedRef.current);
      } else if (isSubscribedSession) {
        // Target only the streaming bubble (split mode may have finalized earlier ones)
        setMessagesRef.current((prev) =>
          prev.map((m) =>
            m.runId === runId && m.role === 'assistant' && m.isStreaming
              ? { ...m, isStreaming: false, content: m.content || `⚠️ ${normalizedErrorMessage}`, toolCalls: m.toolCalls }
              : m,
          ),
        );
        lastSubagentDeltaTimeRef.current.delete(runId);
      }
    });

    // ── User Message Sync (multi-client) ──
    handler.onUserMessage((runId, text, sessionKey, timestamp) => {
      // Only handle messages for the active session
      const isActiveSession = sessionKey === activeSessionKeyRef.current;
      if (!isActiveSession) return;

      const displayText = stripQueueDirectiveForDisplay(text);

      // Deduplicate: if this client sent the message, it already has a local bubble.
      setMessagesRef.current((prev) => {
        if (prev.some((m) => m.runId === runId && (m.role === 'assistant' || m.role === 'user'))) {
          return prev;
        }

        const hasPendingPlaceholder = prev.some(
          (m) => m.role === 'assistant' && m.isStreaming && m.runId?.startsWith('local-') && !m.content,
        );
        if (hasPendingPlaceholder) {
          return prev;
        }

        const gw = gatewaysRef.current.find((g) => g.id === gwId);
        return [...prev, {
          id: `sync-user-${runId}-${Date.now()}`,
          role: 'user' as const,
          content: displayText,
          timestamp: timestamp
            ? new Date(timestamp).toISOString()
            : new Date().toISOString(),
          isHistory: false,
          isStreaming: false,
          ttsStatus: 'none' as const,
          runId,
          gatewayId: gwId,
          gatewayName: gw?.name,
        }];
      });
    });

    // ── chat.side_result (BTW responses) ──
    handler.onSideResult((payload) => {
      // Only display side results for the active session
      if (payload.sessionKey !== activeSessionKeyRef.current) return;

      const gw = gatewaysRef.current.find((g) => g.id === gwId);
      const text = payload.text?.trim();
      if (!text) return;

      // Insert the BTW response as an assistant message
      setMessagesRef.current((prev) => [...prev, {
        id: `btw-${payload.runId}-${payload.seq}-${Date.now()}`,
        role: 'assistant' as const,
        content: text,
        timestamp: new Date(payload.ts).toISOString(),
        isHistory: false,
        isStreaming: false,
        ttsStatus: 'none' as const,
        runId: payload.runId,
        gatewayId: gwId,
        gatewayName: gw?.name,
      }]);

      // TTS for BTW responses (active session, voice active, not muted)
      if (gwId === activeGatewayIdRef.current && !speakerMutedRef.current && isActivatedRef.current) {
        voiceRef.current.speak(text);
      }
    });

    // ── Gateway-level events (shutdown, presence, update, approvals) ──
    handler.onShutdown((payload) => {
      gatewayEventsRef.current.handleShutdown(payload);
    });

    handler.onPresence((payload) => {
      gatewayEventsRef.current.handlePresence(payload);
    });

    handler.onUpdateAvailable((payload) => {
      gatewayEventsRef.current.handleUpdateAvailable(payload);
    });

    handler.onExecApprovalRequested((payload) => {
      gatewayEventsRef.current.handleExecApprovalRequested(payload);
    });

    handler.onExecApprovalResolved((payload) => {
      gatewayEventsRef.current.handleExecApprovalResolved(payload);
    });

    // ── sessions.changed — real-time session list updates ──
    handler.onSessionsChanged((_payload) => {
      // Trigger a session list refresh. This replaces the purely
      // event-polling-based approach in useSessionManager for these events.
      // The session manager already handles chat/agent events; this adds
      // coverage for lifecycle events that don't produce chat/agent frames.
      sessionManagerRef.current.refreshAll().catch(() => {});
    });

    return handler;
  }, [doExtractSentences, appendTtsDebugSentence, getClient, markToolsDone]);

  // ── Sync StreamingHandlers with connected gateways ──
  // Runs when connectionStates change. Creates handlers for newly connected
  // gateways and removes handlers for disconnected ones.
  useEffect(() => {
    const handlers = streamingHandlersRef.current;

    for (const gw of gateways) {
      const status = connectionStates.get(gw.id);
      const hasHandler = handlers.has(gw.id);

      if (status === 'connected' && !hasHandler) {
        // New connection — create handler
        const client = getClient(gw.id);
        if (client) {
          handlers.set(gw.id, wireStreamingHandler(gw.id, client));
        }
      } else if (status !== 'connected' && hasHandler) {
        // Disconnected — destroy handler
        handlers.get(gw.id)!.destroy();
        handlers.delete(gw.id);
      }
    }

    // Also remove handlers for gateways that no longer exist
    for (const [gwId, handler] of handlers) {
      if (!gateways.some((g) => g.id === gwId)) {
        handler.destroy();
        handlers.delete(gwId);
      }
    }
  }, [gateways, connectionStates, getClient, wireStreamingHandler]);

  // ── Cleanup all handlers on unmount ──
  useEffect(() => {
    return () => {
      for (const [, handler] of streamingHandlersRef.current) {
        handler.destroy();
      }
      streamingHandlersRef.current.clear();
      // Clean up Phase 2 managers
      turnProtocolRef.current?.destroy();
      bargeInRef.current?.destroy();
      turnArbiterRef.current?.destroy();
    };
  }, []);

  // ── Gateway Setup Handler ──
  const handleSetupConnect = useCallback(
    async (url: string, token: string, name: string) => {
      try {
        await addGateway(url, token, name || undefined);
        setShowAddGateway(false);
        setScreen('app');
      } catch (err) {
        console.warn('[App] Setup connect error (gateway saved):', err);
        setShowAddGateway(false);
        setScreen('app');
      }
    },
    [addGateway],
  );

  // ── Track pairing state for focused gateway ──
  useEffect(() => {
    if (isPairing && activeGateway) {
      setPairingState({
        status: 'pending',
        deviceId: activeGateway.device?.id ?? '',
        gatewayId: activeGateway.id,
        startedAt: Date.now(),
        remainingMs: 5 * 60 * 1000,
      });
      setPairingGateway(activeGateway);
      setScreen('pairing');
    } else if (isConnected && screen === 'pairing') {
      setPairingState(null);
      setPairingGateway(null);
      setScreen('app');
    }
  }, [isPairing, isConnected, activeGateway, screen]);

  // ── Update countdown timer for pairing ──
  useEffect(() => {
    if (!pairingState || pairingState.status !== 'pending') return;
    const interval = setInterval(() => {
      setPairingState(prev => {
        if (!prev || prev.status !== 'pending') return prev;
        const remaining = Math.max(0, 5 * 60 * 1000 - (Date.now() - prev.startedAt));
        if (remaining === 0) {
          return { ...prev, status: 'expired', remainingMs: 0 };
        }
        return { ...prev, remainingMs: remaining };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [pairingState?.status, pairingState?.startedAt]);

  // ── Wire FSM connection state (any gateway connected = WS_CONNECTED) ──
  useEffect(() => {
    if (isAnyConnected) {
      send({ type: 'WS_CONNECTED' });
      // Clear shutdown state on successful reconnection
      gatewayEvents.dismissShutdown();
    } else {
      send({ type: 'WS_DISCONNECTED' });
    }
  }, [isAnyConnected, send, gatewayEvents]);

  // ── Load history per gateway when connected ──
  useEffect(() => {
    for (const gw of gateways) {
      const status = connectionStates.get(gw.id);
      if (status !== 'connected') continue;
      if (historyLoadedRef.current.has(gw.id)) continue;

      const client = getClient(gw.id);
      if (!client) continue;

      historyLoadedRef.current.add(gw.id);
      // Use session manager's key for active gateway, default for others
      const sessionKey = gw.id === activeGateway?.id
        ? sessionManager.activeSessionKey
        : (gw.sessionKey ?? DEFAULT_SESSION_KEY);

      fetchChatHistoryAsMessages(client, sessionKey, 30, gw.id, gw.name)
        .then((historyMessages) => {
          if (historyMessages.length > 0) {
            loadHistory(historyMessages, gw.id);
          }
        })
        .catch((err) => console.warn(`[App] Failed to load history for ${gw.name}:`, err));
    }
  }, [gateways, connectionStates, getClient, loadHistory]);

  // ── Init TTS config + turn protocol when focused gateway connects ──
  useEffect(() => {
    if (wsClient && isConnected) {
      voice.initTtsConfig().catch((err) =>
        console.warn('[App] Failed to fetch TTS config:', err),
      );

      // Configure turn protocol with active gateway's server capabilities.
      // If the gateway advertises 'voice-turns' in its hello-ok caps, the
      // protocol manager enables chat.turn.* frames. Otherwise it's a
      // silent noop (can still be force-enabled via localStorage).
      turnProtocolRef.current?.configure(
        wsClient,
        sessionManager.activeSessionKey,
        activeGatewayCaps,
      );
      // Update arbiter's protocol ref if already created
      if (turnArbiterRef.current && turnProtocolRef.current) {
        turnArbiterRef.current.setProtocol(turnProtocolRef.current);
      }

      const appVersion = typeof __FRONTEND_VERSION__ !== 'undefined' ? __FRONTEND_VERSION__ : 'unknown';
      remoteLog('lifecycle', 'app_startup', { gwCount: gateways.length, version: appVersion });
      // Startup is done — apply user's remote-logging preference from now on
      notifyStartupComplete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsClient, isConnected]);

  // ── Auto-connect ALL gateways on mount ──
  useEffect(() => {
    if (hasGateways) {
      void connectAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Auto-grow textarea height ──
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, []);

  const resetTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, []);

  // ── Send text message (to focused gateway) ──
  const handleTextSubmit = useCallback(async () => {
    const text = textInput.trim();
    if (!text || !wsClient || !isConnected || !activeGateway) return;

    setTextInput('');
    resetTextareaHeight();

    remoteLog('placeholder', 'dbg_text_send', { text: text.substring(0, 50) });
    playAudioCue('speechEndThinking', isSpeakerMuted, isActivated);

    try {
      await sendUserChatMessage({
        sessionKey: sessionManager.activeSessionKey,
        displayMessage: text,
        gatewayId: activeGateway.id,
        gatewayName: activeGateway.name,
        source: 'text',
      });
    } catch (err) {
      console.error('[App] Failed to send message:', err);
      playAudioCue('error', isSpeakerMuted, isActivated);
    }
  }, [textInput, wsClient, isConnected, activeGateway, sessionManager.activeSessionKey, resetTextareaHeight, isSpeakerMuted, isActivated, sendUserChatMessage]);

  // ── Abort current request ──
  const handleAbort = useCallback(async () => {
    if (!wsClient || !isConnected) return;
    try {
      await abortChat(wsClient, sessionManager.activeSessionKey);
    } catch (err) {
      console.warn('[App] Abort failed:', err);
    }
    voice.stopSpeaking();
    send({ type: 'ABORT' });
  }, [wsClient, isConnected, sessionManager.activeSessionKey, voice, send]);

  // ── Orb Tap Handler ──
  const handleOrbTap = useCallback(async () => {
    unlockAudio();
    await ensureAudioContextResumed();

    switch (displayState) {
      case 'idle': {
        if (!isConnected) return;
        if (!isActivated) {
          send({ type: 'APP_ACTIVATED' });
          startForegroundService();
        }
        try {
          await voice.startListening();
          send({ type: 'INPUT_ACTIVATE' });
          playAudioCue('listeningStarted', isSpeakerMuted, true);
        } catch (err) {
          console.error('[App] Failed to start listening:', err);
        }
        break;
      }
      case 'listening': {
        voice.stopListening();
        // Cancel any pending arbiter turn when deactivating.
        turnArbiterRef.current?.forceCancel('deactivate', { flushDeferredTts: false });
        send({ type: 'INPUT_DEACTIVATE' });
        send({ type: 'APP_DEACTIVATED' });
        stopForegroundService();
        break;
      }
      case 'accumulating': {
        // User taps orb while accumulating → force commit the draft turn
        const arbiter = turnArbiterRef.current;
        if (arbiter && arbiter.isAccumulating) {
          arbiter.forceCommit();
        } else {
          // No draft — just go back to listening
          send({ type: 'TURN_CANCELLED' });
        }
        break;
      }
      case 'speaking': {
        ttsSuppressionRef.current = currentRunIdRef.current;
        voice.stopSpeaking();
        bargeInRef.current?.reset(); // Reset barge-in on explicit user stop
        remoteLog('tts', 'voice_interrupt', { triggerSource: 'user-button', result: 'tts-stopped' });
        send({ type: 'TTS_CANCEL', reason: 'user-interrupt' });
        if (fsmValue.input !== 'inactive') {
          try { await voice.startListening(); } catch { /* ignore */ }
        }
        break;
      }
      case 'thinking':
      case 'streaming': {
        await handleAbort();
        break;
      }
    }
  }, [displayState, isConnected, isActivated, send, voice, fsmValue, handleAbort]);

  // ── Wire VAD speech events to FSM + Barge-In ──
  // Phase 2: Speech during TTS goes through graduated barge-in:
  //   1. Duck TTS immediately on speech start
  //   2. Wait for STT segment or sustained speech → promote to real interrupt
  //   3. False alarm (no STT content) → unduck and resume TTS
  useVoiceSpeakingEffects({
    isSpeaking: voice.isSpeaking,
    isTTSPlaying: voice.isTTSPlaying,
    voiceInterruptEnabled: fsmContext.voiceInterrupt,
    currentRunId: currentRunIdRef.current,
    send,
    getOrCreateTurnArbiter: getOrCreateVoiceSpeechTurnArbiter,
    getOrCreateBargeIn,
    bargeIn: bargeInRef.current,
  });

  // ── Stop TTS and cancel turn arbiter when app deactivates ──
  useEffect(() => {
    if (!isActivated) {
      voice.stopSpeaking();
      voiceCommandTurnRef.current?.reset();
      // Reset barge-in state
      bargeInRef.current?.reset();
      // Cancel any pending turn arbiter draft or pre-segment speech cycle.
      if (turnArbiterRef.current?.hasPendingTurn) {
        turnArbiterRef.current.forceCancel('deactivate', { flushDeferredTts: false });
      }
      // Clear any pending sentence flush
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      sentenceBufferRef.current = '';
    }
  }, [isActivated, voice]);

  const pendingResponseCompleteCueRef = useRef(false);

  // Keep refs in sync so the TTS effect can read current values without
  // listing them as deps (which would cause spurious re-runs mid-TTS).
  isSpeakingRef.current = voice.isSpeaking;
  voiceInterruptRef.current = fsmContext.voiceInterrupt;

  // ── Wire TTS playing state to FSM + Barge-In ──
  useEffect(() => {
    if (voice.isTTSPlaying && !prevTTSPlayingRef.current) {
      // false→true: TTS just started
      send({ type: 'TTS_SEGMENT_START' });
      // Always ensure the manager exists and transitions to monitoring state.
      // Using getOrCreateBargeIn() here (not bargeInRef.current?.) so that
      // speechDetected() below is guaranteed to find the manager in 'monitoring'
      // state, even if this is the first TTS segment of the session.
      const bi = getOrCreateBargeIn();
      bi.ttsStarted();
      // If the user is already speaking when TTS starts, trigger barge-in
      // immediately — useVoiceSpeakingEffects won't re-fire for this utterance
      // because isSpeaking didn't transition.
      if (isSpeakingRef.current && voiceInterruptRef.current) {
        bi.speechDetected();
      }
    } else if (!voice.isTTSPlaying && prevTTSPlayingRef.current) {
      // true→false: TTS just stopped
      bargeInRef.current?.ttsStopped();
      if (fsmValue.playback === 'playing') {
        send({ type: 'TTS_QUEUE_EMPTY' });
        if (fsmValue.request === 'idle') {
          pendingResponseCompleteCueRef.current = false;
          // "Response complete" cue — TTS finished, back to listening
          playAudioCue('responseComplete', isSpeakerMuted, isActivated);
        } else {
          pendingResponseCompleteCueRef.current = true;
        }
      }
    }
    prevTTSPlayingRef.current = voice.isTTSPlaying;
  }, [voice.isTTSPlaying, getOrCreateBargeIn, send, fsmValue.playback, fsmValue.request, isSpeakerMuted, isActivated]);

  useEffect(() => {
    if (
      pendingResponseCompleteCueRef.current
      && fsmValue.request === 'idle'
      && fsmValue.playback === 'idle'
      && !voice.isTTSPlaying
    ) {
      pendingResponseCompleteCueRef.current = false;
      playAudioCue('responseComplete', isSpeakerMuted, isActivated);
    }
  }, [voice.isTTSPlaying, fsmValue.request, fsmValue.playback, isSpeakerMuted, isActivated]);

  // ── Model switch ──
  const handleModelChange = useCallback(async (modelId: string) => {
    chatSwitchModel(modelId);
    if (wsClient && isConnected) {
      try {
        await switchModelWS(wsClient, sessionManager.activeSessionKey, modelId);
      } catch (err) {
        console.warn('[App] Model switch failed:', err);
      }
    }
  }, [wsClient, isConnected, sessionManager.activeSessionKey, chatSwitchModel]);

  // ── API Key change handler ──
  const handleApiKeyChange = useCallback(() => {
    voice.refreshProvider();
  }, [voice]);

  // ── Voice Interrupt toggle ──
  const handleToggleVoiceInterrupt = useCallback(() => {
    const newVal = !fsmContext.voiceInterrupt;
    writeString(STORAGE_KEYS.VOICE_INTERRUPT, String(newVal));
    send({ type: 'VOICE_INTERRUPT_TOGGLE', value: newVal });
    remoteLog('toggle', 'voice_interrupt', { enabled: newVal });
  }, [fsmContext.voiceInterrupt, send]);

  // ── Mute toggle handlers ──
  const handleToggleSpeakerMute = useCallback(() => {
    const newMuted = !isSpeakerMuted;
    writeString(STORAGE_KEYS.SPEAKER_MUTED, String(newMuted));
    send({ type: 'TOGGLE_SPEAKER_MUTE' });
    remoteLog('toggle', 'speaker_mute', { muted: newMuted });
    if (newMuted) {
      voice.stopSpeaking();
    }
  }, [isSpeakerMuted, send, voice]);

  const handleToggleMicMute = useCallback(() => {
    const newMuted = !isMicMuted;
    writeString(STORAGE_KEYS.MIC_MUTED, String(newMuted));
    send({ type: 'TOGGLE_MIC_MUTE' });
    remoteLog('toggle', 'mic_mute', { muted: newMuted });
    if (newMuted) {
      // Stop VAD/STT but keep the app active — micMode region tracks the mute state,
      // and the isMicMutedRef guard in handleVoiceTranscript prevents transcript submission
      voice.stopListening();
    } else if (isActivated) {
      // Unmuting while active — restart the voice pipeline
      voice.startListening().catch((err) => {
        console.error('[App] Failed to restart listening on unmute:', err);
      });
    }
  }, [isMicMuted, send, voice, isActivated]);

  // ── Pause main voice pipeline during Voice Lock training ──
  // Training opens its own mic/VAD. If the main pipeline is also active,
  // the user's training sentences get transcribed and sent to the assistant.
  useEffect(() => {
    if (voiceLock.isTraining) {
      // Training started — pause main pipeline if it's active
      if (voice.isVADActive) {
        wasListeningBeforeTrainingRef.current = true;
        voice.stopListening();
        // Also stop any TTS that might interfere with training audio
        voice.stopSpeaking();
      } else {
        wasListeningBeforeTrainingRef.current = false;
      }
    } else {
      // Training stopped — resume if we were listening before
      if (wasListeningBeforeTrainingRef.current && isActivated && !isMicMuted) {
        wasListeningBeforeTrainingRef.current = false;
        voice.startListening().catch((err) => {
          console.error('[App] Failed to restart listening after voice lock training:', err);
        });
      }
    }
  }, [voiceLock.isTraining, voice, isActivated, isMicMuted]);



  // ── Gateway management handlers ──
  const handleGatewaySelect = useCallback(async (id: string) => {
    await switchGateway(id);
  }, [switchGateway]);

  const handleGatewayRemove = useCallback((id: string) => {
    removeGatewayAction(id);
    // Clear history tracking for removed gateway
    historyLoadedRef.current.delete(id);
  }, [removeGatewayAction]);

  const handleGatewayAdd = useCallback(() => {
    setShowAddGateway(true);
    setSettingsOpen(false);
  }, []);

  // ── Pairing handlers ──
  const handlePairingCancel = useCallback(() => {
    setPairingState(null);
    setPairingGateway(null);
    setScreen(hasGateways ? 'app' : 'setup');
  }, [hasGateways]);

  const handlePairingRetry = useCallback(() => {
    void reconnect();
    setPairingState(prev => prev ? {
      ...prev,
      status: 'pending',
      startedAt: Date.now(),
      remainingMs: 5 * 60 * 1000,
    } : null);
  }, [reconnect]);

  // ── Render ──

  // Initial setup (no gateways configured yet) — show polished onboarding
  if (screen === 'setup' && !hasGateways) {
    return (
      <>
        <Welcome
          onSignIn={() => onShowLogin?.()}
          onConnect={handleSetupConnect}
          isAuthenticated={isAuthenticated}
          userEmail={user?.email}
        />
        <span className={styles.version}>{versionLabel}</span>
        {previewBadge}
        {updateAvailable && (
          <div className={styles.updateBanner} onClick={() => forceUpdate()}>
            New version available — tap to refresh
          </div>
        )}
      </>
    );
  }

  // Add gateway overlay (from settings)
  if (showAddGateway) {
    return (
      <>
        <GatewaySetup
          onConnect={handleSetupConnect}
          onCancel={() => {
            setShowAddGateway(false);
          }}
        />
        <span className={styles.version}>{versionLabel}</span>
        {previewBadge}
      </>
    );
  }

  // Pairing screen
  if (screen === 'pairing' && pairingState && pairingGateway) {
    return (
      <PairingApproval
        state={pairingState}
        onCancel={handlePairingCancel}
        onRetry={handlePairingRetry}
      />
    );
  }

  // Origin blocked screen
  if (connectionState === 'origin_blocked' && activeGateway) {
    return (
      <OriginBlockedOverlay
        gatewayName={activeGateway.name}
        onRetry={reconnect}
        onDisconnect={() => removeGatewayAction(activeGateway.id)}
      />
    );
  }

  return (
    <div className={styles.appContainer}>
      {/* Content area (chat + inputs — left side in landscape) */}
      <div className={styles.contentArea}>
      {/* Top status bar — non-interactive status info */}
      {!privacyActive && (
        <div className={styles.topBar}>
          <span className={styles.version}>{versionLabel}</span>
          {(() => {
            const badge = getEnvironmentBadge();
            return badge ? (
              <span className={styles.envBadge} style={{ background: badge.color }}>
                {badge.label}
              </span>
            ) : null;
          })()}
          {previewBadge}
          <div className={styles.statusRow}>
            <span className={`${styles.statusDot} ${isConnected ? styles.statusConnected : styles.statusDisconnected}`} />
            <span className={styles.statusLabel}>
              {gateways.length > 1 ? activeGateway?.name ?? 'No Gateway' : connectionStatus}
              <span
                className={`${styles.sessionIndicator} ${isNonMainSession ? styles.sessionIndicatorTappable : ''}`}
                onClick={isNonMainSession ? handleBackToMain : undefined}
              >
                {' · '}
                {isNonMainSession && '← '}
                {sessionManager.activeSessionKey.split(':')[1] ?? 'main'}
              </span>
            </span>
          </div>
          {voiceLock.isEnrolled && voiceLock.liveScore !== null && (
            <span
              className={`${styles.voiceLockScore} ${voiceLock.liveScore < voiceLock.threshold ? styles.voiceLockBadgeFail : ''}`.trim()}
            >
              🔒 {voiceLock.liveScore.toFixed(2)}
            </span>
          )}
          <PresenceIndicator presence={gatewayEvents.presence} />
        </div>
      )}

      {/* Gateway event banners — shutdown, update, approvals */}
      {!privacyActive && gatewayEvents.shutdown && (
        <ShutdownBanner
          shutdown={gatewayEvents.shutdown}
          onDismiss={gatewayEvents.dismissShutdown}
        />
      )}
      {!privacyActive && gatewayEvents.gatewayUpdateAvailable && (
        <GatewayUpdateBadge
          currentVersion={gatewayEvents.gatewayUpdateAvailable.currentVersion}
          latestVersion={gatewayEvents.gatewayUpdateAvailable.latestVersion}
          onDismiss={gatewayEvents.dismissGatewayUpdate}
        />
      )}
      {!privacyActive && gatewayEvents.pendingApprovals.length > 0 && (
        <ExecApprovalNotification approvals={gatewayEvents.pendingApprovals} />
      )}

      {/* Privacy mode: subtle exit button */}
      {privacyActive && <PrivacyExitButton onExit={dismissPrivacy} />}

      {/* Gateway bar (only with multiple gateways) */}
      {!privacyActive && (
        <GatewayBar
          gateways={gateways}
          activeGatewayId={activeGateway?.id ?? null}
          connectionStates={connectionStates}
          onSelect={(id) => void handleGatewaySelect(id)}
        />
      )}

      {/* Conversation viewport: chat/board with a compact docked quick-switch lane */}
      {!privacyActive && (
        <div
          className={[
            styles.conversationViewport,
            quickSwitchSide === 'left'
              ? styles.conversationViewportRailLeft
              : styles.conversationViewportRailRight,
          ].join(' ')}
        >
          <div className={styles.conversationMain}>
            {/* Chat area (visible when Chat view is active) */}
            {activeView === 'chat' && (
              <Chat
                messages={messages}
                showTimestamps={showTimestamps}
                showGatewayBadge={showGatewayBadge}
                scrollToBottomTrigger={chat.historyLoadCount}
              />
            )}

            {/* Board view (visible when Board view is active) */}
            {activeView === 'board' && (
              <TaskBoard />
            )}
          </div>

          <SessionQuickSwitchRail
            items={sessionQuickSwitch.items}
            side={quickSwitchSide}
            onSessionSelect={(sessionKey) => sessionManager.switchSession(sessionKey)}
            onItemsReorder={sessionQuickSwitch.reorderItems}
          />
        </div>
      )}

      {/* View Switcher */}
      {!privacyActive && (
        <ViewSwitcher activeView={activeView} onSwitch={switchView} />
      )}

      {/* Upload error banner */}
      {!privacyActive && uploadErrorText && (
        <div className={styles.uploadErrorBanner} role="status" aria-live="polite">
          {uploadErrorText}
        </div>
      )}

      {/* Text input bar */}
      {!privacyActive && (
        <div className={styles.textInputBar}>
          {/* Hidden file input — native change listener attached via useEffect below */}
          <input
            ref={fileInputRef}
            type="file"
            accept="*/*"
            multiple
            className={styles.visuallyHidden}
          />
          <button
            className={styles.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={!isConnected || isUploading}
            aria-label="Attach file"
          >
            {isUploading ? '⏳' : '📎'}
          </button>
          <textarea
            ref={textareaRef}
            className={styles.textInput}
            value={textInput}
            rows={1}
            onChange={(e) => {
              setTextInput(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void handleTextSubmit();
              }
            }}
            placeholder={
              isUploading
                ? (uploadStatusText || 'Uploading…')
                : isConnected
                  ? sessionTokens > 0 && messageFieldContextWindow !== null
                    ? `${fmtTokens(sessionTokens)} / ${fmtTokens(messageFieldContextWindow)} tokens`
                    : gateways.length > 1
                      ? `Message ${activeGateway?.name ?? 'gateway'}…`
                      : 'Type a message…'
                  : 'Not connected'
            }
            disabled={!isConnected}
          />
          <button
            className={styles.sendBtn}
            onClick={() => void handleTextSubmit()}
            disabled={!textInput.trim() || !isConnected}
            aria-label="Send message"
          >
            ➤
          </button>
        </div>
      )}

      {/* Agent Ticker — stays at bottom of content area */}
      {!privacyActive && (
        <AgentTicker
          items={agentTicker.items}
          peek={agentTicker.peek}
          hasCompleted={agentTicker.hasCompleted}
          subscribedSessions={agentTicker.subscribedSessions}
          onItemTap={agentTicker.openPeek}
          onItemDismiss={agentTicker.dismiss}
          onDismissAllCompleted={agentTicker.dismissAllCompleted}
          onPeekClose={agentTicker.closePeek}
          onFocus={handleTickerFocus}
          onSubscribe={agentTicker.subscribe}
          onUnsubscribe={agentTicker.unsubscribe}
        />
      )}

      {/* Back to Main bar — only for sub-agent sessions */}
      {!privacyActive && isSubagentSession && (
        <button className={styles.backToMainBar} onClick={handleBackToMain}>
          ← Back to Main
        </button>
      )}
      </div>

      {/* Control strip: orb + buttons (bottom in portrait, right side in landscape) */}
      {!isKeyboardVisible && (
      <div className={`${styles.bottomArea} ${privacyActive ? styles.bottomAreaPrivacy : ''}`}
        onClickCapture={(e) => {
          const el = e.target as HTMLElement;
          remoteLog('reload', 'bottomArea_click', { tag: el.tagName, className: el.className?.substring?.(0, 50) || '', text: el.textContent?.substring(0, 20) || '', ariaLabel: el.getAttribute?.('aria-label') || '' });
        }}
      >
        <div className={styles.orbRow}>
          {/* Left column — app controls */}
          <div className={styles.sideColumn}>
            {!privacyActive && (
              <>
                <button
                  className={`${styles.controlBtn} ${sessionPanelOpen ? styles.controlActive : ''}`}
                  onClick={() => {
                    setSessionPanelOpen(!sessionPanelOpen);
                    setSettingsOpen(false);
                  }}
                  aria-label="Agents & Sessions"
                >
                  💬
                </button>
                <button
                  className={styles.controlBtn}
                  onClick={() => {
                    remoteLog('reload', 'button_clicked', {});
                    window.location.reload();
                  }}
                  aria-label="Reload"
                >
                  🔄
                </button>
                <button
                  className={styles.controlBtn}
                  onClick={() => {
                    setSettingsOpen(!settingsOpen);
                    setSessionPanelOpen(false);
                  }}
                  aria-label="Settings"
                >
                  ⚙️
                </button>
                <button
                  className={`${styles.controlBtn} ${voiceLock.gateEnabled ? styles.controlActive : ''} ${voiceLock.gateEnabled && !voiceLock.isEnrolled ? styles.controlDimmed : ''}`}
                  onClick={() => voiceLock.setGateEnabled(!voiceLock.gateEnabled)}
                  aria-label={voiceLock.gateEnabled ? 'Disable audio gate' : 'Enable audio gate'}
                  title={
                    voiceLock.gateEnabled
                      ? voiceLock.isEnrolled
                        ? 'Audio gate ON'
                        : 'Audio gate ON (no profile)'
                      : 'Audio gate OFF'
                  }
                >
                  {voiceLock.gateEnabled ? '🔒' : '🔓'}
                </button>
              </>
            )}
          </div>

          <Orb
            displayState={displayState}
            statusText={privacyActive ? '' : statusText}
            onTap={() => void handleOrbTap()}
          />

          {/* Right column — voice controls */}
          <div className={styles.sideColumn}>
            <button
              className={`${styles.controlBtn} ${isSpeakerMuted ? styles.mutedBtn : ''}`}
              onClick={handleToggleSpeakerMute}
              aria-label={isSpeakerMuted ? 'Unmute speaker' : 'Mute speaker'}
            >
              {isSpeakerMuted ? '🔇' : '🔊'}
            </button>
            <button
              className={`${styles.controlBtn} ${isMicMuted ? styles.mutedBtn : ''}`}
              onClick={handleToggleMicMute}
              aria-label={isMicMuted ? 'Unmute microphone' : 'Mute microphone'}
            >
              🎤
            </button>
            {!privacyActive && (
              <>
                {/* TODO: restore stop button when tool-call cancellation is implemented
                {isPlaying && (
                  <button
                    className={`${styles.controlBtn} ${styles.stopBtn}`}
                    onClick={() => {
                      voice.stopSpeaking();
                      send({ type: 'TTS_CANCEL', reason: 'stop-button' });
                    }}
                    aria-label="Stop playback"
                  >
                    ⏹
                  </button>
                )}
                */}
                <button
                  className={`${styles.controlBtn} ${voice.playbackSpeed !== 1 ? styles.controlActive : ''}`}
                  onClick={() => voice.cycleSpeed()}
                  aria-label={`Speed ${voice.playbackSpeed}x`}
                >
                  {voice.playbackSpeed}×
                </button>
                <button
                  className={`${styles.controlBtn} ${fsmContext.voiceInterrupt ? styles.controlActive : ''}`}
                  onClick={handleToggleVoiceInterrupt}
                  aria-label={fsmContext.voiceInterrupt ? 'Disable voice interrupt' : 'Enable voice interrupt'}
                  title={fsmContext.voiceInterrupt ? 'Voice interrupt ON' : 'Voice interrupt OFF'}
                >
                  🎧
                </button>
              </>
            )}
          </div>
        </div>



      </div>
      )}

      {/* Voice Lock test overlay */}
      {voiceLock.isTesting && (
        <VoiceLockTest
          testScore={voiceLock.testScore}
          audioLevel={voiceLock.audioLevel}
          threshold={voiceLock.threshold}
          onClose={voiceLock.exitTestMode}
        />
      )}

      {/* Settings panel (with multi-gateway management) — hidden in privacy mode */}
      {!privacyActive && (
        <Settings
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          selectedModel={selectedModel}
          onModelChange={(m) => void handleModelChange(m)}
          privacyMode={privacyActive}
          onPrivacyToggle={togglePrivacy}
          showTimestamps={showTimestamps}
          onTimestampsToggle={() => setShowTimestamps((prev) => !prev)}
          connectionStatus={connectionStatus}
          gatewayConnectionState={connectionState}
          isRequestActive={isRequestActive}
          sttProvider={voice.sttProvider}
          onApiKeyChange={handleApiKeyChange}
          gateways={gateways}
          activeGatewayId={activeGateway?.id ?? null}
          connectionStates={connectionStates}
          onGatewaySelect={(id) => void handleGatewaySelect(id)}
          onGatewayRemove={handleGatewayRemove}
          onGatewayAdd={handleGatewayAdd}
          onSignIn={onShowLogin}
          gatewayVersion={gatewayVersion}
          gatewayClient={wsClient}
          activeGatewayName={activeGateway?.name ?? null}
          activeSessionKey={sessionManager.activeSessionKey}
          voiceLock={voiceLock}
        />
      )}

      {/* Session panel — hidden in privacy mode */}
      {!privacyActive && (
        <SessionPanel
          isOpen={sessionPanelOpen}
          onClose={() => setSessionPanelOpen(false)}
          sessionsByAgent={overviewSessionsByAgent}
          agents={sessionManager.agents}
          activeSessionKey={sessionManager.activeSessionKey}
          onSessionSelect={(key) => sessionManager.switchSession(key)}
          onSessionRename={async (key, label) => {
            sessionQuickSwitch.renameSession(key, label);
            return true;
          }}
          onSessionSpokenAliasesSave={async (key, aliases) => {
            sessionQuickSwitch.setSpokenAliases(key, aliases);
            return true;
          }}
          onSessionCreate={(params) => sessionManager.createSessionData(params)}
          onSessionArchive={async (key) => {
            const fallbackKey = chooseOverviewFallbackSessionKey(key);
            sessionQuickSwitch.archiveSession(key, true);
            if (sessionManager.activeSessionKey === key && fallbackKey) {
              sessionManager.switchSession(fallbackKey);
            }
            return true;
          }}
          hasLocalSessionName={sessionQuickSwitch.hasLocalCustomLabel}
          onTogglePinned={sessionQuickSwitch.togglePinned}
          pinnedSessionKeys={sessionQuickSwitch.pinnedSessionKeys}
          onRefresh={() => sessionManager.refreshAll()}
          loading={sessionManager.loading}
          error={sessionManager.activeSessionData?.error}
          gatewayName={activeGateway?.name}
          defaultAgentId={sessionManager.activeSessionData?.defaultAgentId}
        />
      )}

      {/* Update banner — hidden in privacy mode */}
      {!privacyActive && updateAvailable && (
        <div className={styles.updateBanner} onClick={() => forceUpdate()}>
          New version available — tap to refresh
        </div>
      )}
    </div>
  );
}

/**
 * Load cloud settings from the backend and merge them into localStorage.
 *
 * Delegates to `restoreCloudSettings()` from the settings registry for
 * consistent merge behavior. See registry.ts for merge rules.
 *
 * Returns the number of NEW gateways added from cloud (0 = no new ones).
 */
async function loadCloudSettings(getAuthHeaders: () => Record<string, string>): Promise<number> {
  try {
    const baseUrl = getApiUrl();
    const headers = getAuthHeaders();
    if (!headers.Authorization) return 0;

    const res = await fetch(`${baseUrl}/api/user/settings`, { headers });
    if (!res.ok) return 0;

    const data = await res.json();
    const settings = data.settings;
    if (!settings || typeof settings !== 'object') return 0;

    return restoreCloudSettings(settings);
  } catch {
    return 0;
  }
}

/**
 * Upload current localStorage settings to the cloud.
 * Uses `collectCloudSettings()` from the settings registry for
 * a consistent set of keys including device keypairs.
 */
async function uploadLocalSettingsToCloud(getAuthHeaders: () => Record<string, string>): Promise<void> {
  try {
    const baseUrl = getApiUrl();
    const headers = getAuthHeaders();
    if (!headers.Authorization) return;

    const settings = collectCloudSettings();

    // Only upload if there's meaningful data
    const hasData = Object.values(settings).some((v) => v !== null);
    if (!hasData) return;

    await fetch(`${baseUrl}/api/user/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ settings }),
    });
  } catch {
    // Silently ignore — non-critical
  }
}

/** Auth-aware wrapper — handles magic link verification, GitHub OAuth callback, onboarding, and optional login */
function AppWithAuth() {
  const { loading: authLoading, isAuthenticated, getAuthHeaders } = useAuth();
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  const [cloudLoading, setCloudLoading] = useState(false);
  const cloudLoadedRef = useRef(false);

  // GitHub OAuth callback state
  const [githubCallbackParams, setGitHubCallbackParams] = useState<URLSearchParams | null>(null);
  const [githubCallbackStatus, setGitHubCallbackStatus] = useState<'processing' | 'success' | 'error' | null>(null);
  const [githubCallbackError, setGitHubCallbackError] = useState<string | null>(null);

  // Check URL for /auth/verify?token=xxx or /github/callback?code=xxx on mount
  useEffect(() => {
    const url = new URL(window.location.href);
    const path = url.pathname;
    const token = url.searchParams.get('token');

    if (path.endsWith('/auth/verify') && token) {
      setVerifyToken(token);
      // Clean the URL
      window.history.replaceState({}, '', url.pathname.replace(/\/auth\/verify$/, '') || '/');
    }

    // GitHub OAuth callback
    if (path.includes('/github/callback') && url.searchParams.has('code')) {
      setGitHubCallbackParams(new URLSearchParams(url.search));
      // Clean the URL
      const cleanPath = import.meta.env.BASE_URL || '/';
      window.history.replaceState({}, '', cleanPath);
    }
  }, []);

  // Cloud sync on sign-in: always load + merge from cloud, then upload merged result
  useEffect(() => {
    if (!isAuthenticated || cloudLoadedRef.current || authLoading) return;
    cloudLoadedRef.current = true;

    setCloudLoading(true);

    // 1. Load cloud settings and merge into local
    loadCloudSettings(getAuthHeaders)
      .then((newGatewaysAdded) => {
        // 2. Upload the merged result back to cloud (includes local-only gateways + keypairs)
        return uploadLocalSettingsToCloud(getAuthHeaders).then(() => newGatewaysAdded);
      })
      .then((newGatewaysAdded) => {
        if (newGatewaysAdded > 0) {
          // New gateways were added from cloud — reload to reinitialize GatewayProvider
          reloadApp();
        }
      })
      .finally(() => setCloudLoading(false));
  }, [isAuthenticated, authLoading, getAuthHeaders]);

  // Handle GitHub OAuth callback
  useEffect(() => {
    if (!githubCallbackParams) return;

    setGitHubCallbackStatus('processing');
    handleGitHubCallback(githubCallbackParams)
      .then(() => {
        setGitHubCallbackStatus('success');
        setGitHubCallbackParams(null);
        // Auto-dismiss success after 2 seconds
        setTimeout(() => setGitHubCallbackStatus(null), 2000);
      })
      .catch((err) => {
        setGitHubCallbackStatus('error');
        setGitHubCallbackError(err instanceof Error ? err.message : 'GitHub connection failed');
        setGitHubCallbackParams(null);
      });
  }, [githubCallbackParams]);

  // Show loading while auth state is being checked or cloud settings are loading
  if (authLoading || cloudLoading) {
    return null; // Brief flash — checks are fast
  }

  // Show verification screen when handling a magic link
  if (verifyToken) {
    return (
      <AuthVerify
        token={verifyToken}
        onComplete={() => setVerifyToken(null)}
      />
    );
  }

  // Show login screen if explicitly requested (from onboarding or settings)
  if (showLogin) {
    return (
      <Login
        onSkip={() => setShowLogin(false)}
        skipLabel="← Back"
      />
    );
  }

  // GitHub OAuth callback status overlay
  if (githubCallbackStatus === 'processing') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', color: '#ccc', gap: 12,
      }}>
        <span style={{ fontSize: 24 }}>🔗</span>
        <span>Connecting GitHub account…</span>
      </div>
    );
  }

  if (githubCallbackStatus === 'error') {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', color: '#ccc', gap: 12,
      }}>
        <span style={{ fontSize: 24 }}>⚠️</span>
        <span>GitHub connection failed</span>
        <span style={{ fontSize: 13, color: '#888' }}>{githubCallbackError}</span>
        <button
          onClick={() => setGitHubCallbackStatus(null)}
          style={{
            marginTop: 8, padding: '8px 20px', background: '#222',
            border: '1px solid #444', borderRadius: 8, color: '#ccc',
            cursor: 'pointer', fontSize: 14,
          }}
        >
          Continue
        </button>
      </div>
    );
  }

  return <AppInner onShowLogin={() => setShowLogin(true)} />;
}

export function App() {
  return (
    <GatewayProvider>
      <AppWithAuth />
    </GatewayProvider>
  );
}
